/**
 * Types and functions for working with binary space partitioning trees (shortened BSP trees)
 * @module
 */
import { materialCacheKey, type MaterialSrc } from "common/materials/materialSrc";
import { ensureDefined } from "common/utils/assertOfType";
import type { HatchProperties, HatchMaterial } from "geom/hatchTypes";
import { hatchPropertiesNew } from "geom/hatchTypes";
import { aabb2Height, aabb2Width } from "./aabb2Types";
import { aabb3Copy, aabb3Overlaps, aabb3Union } from "./aabb3";
import type { AABB3 } from "./aabb3Types";
import { aabb3New } from "./aabb3Types";
import type { Face } from "./brep";
import { faceNew } from "./brep";
import { getBSPAllocators } from "./bspMem";
import type {
  LineBSP,
  PlaneBSP,
  PolygonBSP,
  NodeBSP,
  ExtendedDataPolygonBSP,
  LineSplitBSP,
  PolygonSplitBSP,
  IntersectionPointBSP,
  RayIntersectionBSP,
  RayIntersectionPointBSP,
  RenderInfoBSP,
  MaxBSPDepthErrorHandler,
} from "./bspTypes";
import {
  BSP_EPSILON,
  REMOVE_COPLANAR_LINES_EPSILON,
  TraversalOrderBSP,
  TraversalCullingBSP,
  BSP_EPSILON_SQUARED,
} from "./bspTypes";
import { DIST_EPSILON, DIST_EPSILON_SQUARED } from "./core";
import { fillHatchLines, getHatchAABB2 } from "./hatch";
import { line3Eval } from "./line3";
import { lineBSPNew } from "./lineBSPNew";
import {
  PlaneSide,
  planeCopy,
  planeEval,
  planeMove,
  planeMoveAlongNormal,
  planeNaN,
  planeNormal,
  polyLine2OnPlane,
} from "./plane";
import {
  planeBSPCopy,
  planeBSPFlip,
  planeBSPMoveAlongNormal,
  planeBSPMove,
  planeBSPByThreePoints,
  planeBSPByPointAndNormal,
  planeBSPNew,
} from "./planeBSP";
import type { Plane } from "./planeTypes";
import {
  polyLine2AABB2,
  polyLine2Area,
  polyLine2CopySimplify,
  polyLine2EdgeCount,
  polyLine2EdgeEnd,
  polyLine2EdgeStart,
  polyLine2IsConvex,
  polyLine2SetCCW,
} from "./polyLine2";
import type { PolyLine2 } from "./polyLine2Types";
import type { PolyLine3 } from "./polyLine3";
import { polyLine3AABB3, polyLine3ByTwoPoints, polyLine3RemoveDuplicateNeighbors } from "./polyLine3";
import { ray2Project } from "./ray2";
import { pushXYZ, pushXYZXYZ } from "./utils";
import type { UVGeneration, UVTransformation } from "./uv";
import { vec2DistSquared, vec2Normalize } from "./vec2";
import type { Vec3 } from "./vec3";
import { vec3Cross, vec3Dist, vec3DistSquared, vec3Dot, vec3LenSquared, vec3Result, vec3Sub } from "./vec3";

const AREA_TOLERANCE = 1e-6;
const POLYGON_AABB_TOLERANCE = 0.1;
const MAX_BSP_DEPTH = 3000;

// Only used for debugging purposes, to be able to visualize the roof clipping volumes.
// Do not commit this flag set to true to any branch to avoid increasing the memory
// footprint of the BSP tree
export const ADD_DEBUG_POLYGONS_TO_BSP = false;

const emptyLinePlaneBSPResult = {
  x: -0,
  y: -0,
  z: -0,
  t: -0,
};

export function nodeBSPNew(
  plane: PlaneBSP,
  polygons: PolygonBSP[],
  parentNode: NodeBSP | null,
  frontNode: NodeBSP | null,
  backNode: NodeBSP | null,
  userData?: any,
): NodeBSP {
  const allocatorsBSP = getBSPAllocators();
  if (!allocatorsBSP) {
    return {
      plane,
      polygons,
      parentNode,
      frontNode,
      backNode,
      userData,
    };
  }

  const node = allocatorsBSP.allocNode();

  node.plane = plane;
  node.polygons = polygons;
  node.parentNode = parentNode;
  node.frontNode = frontNode;
  node.backNode = backNode;
  node.userData = userData;

  return node;
}

export function polygonBSPNew(
  priority: number,
  vertices: number[],
  plane: PlaneBSP,
  extendedData?: ExtendedDataPolygonBSP,
): PolygonBSP {
  const allocatorsBSP = getBSPAllocators();
  if (!allocatorsBSP) {
    return {
      priority,
      vertices,
      plane,
      extendedData,
    };
  }

  const polygon = allocatorsBSP.allocPolygon();

  polygon.priority = priority;
  polygon.vertices = vertices;
  polygon.plane = plane;
  polygon.extendedData = extendedData;

  return polygon;
}

export function extendedDataPolygonBSPNew(
  id: number,
  aabb: AABB3,
  visibleLines: LineBSP[],
  splitLines: LineBSP[] = [],
  hatchLines: LineBSP[] = [],
  neighborNormals: Vec3[] = [],
  edgePlanes: PlaneBSP[] = [],
  hatchProperties?: HatchProperties,
  entityId?: string,
  propId?: string,
): ExtendedDataPolygonBSP {
  const allocatorsBSP = getBSPAllocators();
  if (!allocatorsBSP) {
    return {
      id,
      aabb,
      visibleLines,
      splitLines,
      hatchLines,
      neighborNormals,
      edgePlanes,
      hatchProperties,
      entityId,
      propId,
    };
  }

  const extendedData = allocatorsBSP.allocExtendedDataPolygon();

  extendedData.id = id;
  extendedData.visibleLines = visibleLines;
  extendedData.splitLines = splitLines;
  extendedData.hatchLines = hatchLines;
  extendedData.neighborNormals = neighborNormals;
  extendedData.edgePlanes = edgePlanes;
  extendedData.aabb = aabb;
  extendedData.hatchProperties = hatchProperties;
  extendedData.entityId = entityId;
  extendedData.propId = propId;

  return extendedData;
}

export function lineSplitBSPNew(frontPolygon: PolygonBSP, line: LineBSP): LineSplitBSP {
  const allocatorsBSP = getBSPAllocators();
  if (!allocatorsBSP) {
    return {
      frontPolygon,
      line,
    };
  }

  const split = allocatorsBSP.allocLineSplit();

  split.frontPolygon = frontPolygon;
  split.line = line;

  return split;
}

export function lineBSPCopy({ x1, y1, z1, x2, y2, z2 }: LineBSP): LineBSP {
  return lineBSPNew(x1, y1, z1, x2, y2, z2);
}

export function polyLine3FromLineBSP({ x1, y1, z1, x2, y2, z2 }: LineBSP): PolyLine3 {
  return polyLine3ByTwoPoints(x1, y1, z1, x2, y2, z2);
}

export function polygonBSPFlip({ priority, vertices, plane, extendedData }: PolygonBSP): PolygonBSP {
  const reversedVertices = new Array<number>(vertices.length);
  for (let i = 0, l = vertices.length; i < l; i += 3) {
    reversedVertices[i] = vertices[l - i - 3];
    reversedVertices[i + 1] = vertices[l - i - 2];
    reversedVertices[i + 2] = vertices[l - i - 1];
  }

  return polygonBSPNew(priority, reversedVertices, planeBSPFlip(plane), extendedData);
}

export function polygonBSPMoveAlongNormal(
  { priority, vertices, plane, extendedData }: PolygonBSP,
  offset: number,
): PolygonBSP {
  let { nX, nY, nZ } = plane;
  nX *= offset;
  nY *= offset;
  nZ *= offset;

  const movedVertices = new Array<number>(vertices.length);
  for (let i = 0, l = vertices.length; i < l; i += 3) {
    movedVertices[i] = vertices[i] + nX;
    movedVertices[i + 1] = vertices[i + 1] + nY;
    movedVertices[i + 2] = vertices[i + 2] + nZ;
  }

  return polygonBSPNew(priority, movedVertices, planeBSPMoveAlongNormal(plane, offset), extendedData);
}

export function polygonBSPMove(
  { priority, vertices, plane, extendedData }: PolygonBSP,
  x: number,
  y: number,
  z: number,
): PolygonBSP {
  const movedVertices = new Array<number>(vertices.length);
  for (let i = 0, l = vertices.length; i < l; i += 3) {
    movedVertices[i] = vertices[i] + x;
    movedVertices[i + 1] = vertices[i + 1] + y;
    movedVertices[i + 2] = vertices[i + 2] + z;
  }

  return polygonBSPNew(priority, movedVertices, planeBSPMove(plane, x, y, z), extendedData);
}

function extendedDataPolygonBSPCopy(extendedData?: ExtendedDataPolygonBSP): ExtendedDataPolygonBSP | undefined {
  if (!extendedData) {
    return undefined;
  }

  const { id, visibleLines, splitLines, neighborNormals, hatchLines, edgePlanes, hatchProperties, entityId, aabb } =
    extendedData;

  return extendedDataPolygonBSPNew(
    id,
    aabb3Copy(aabb),
    visibleLines.slice(),
    splitLines.slice(),
    hatchLines.slice(),
    neighborNormals.slice(),
    edgePlanes.map(planeBSPCopy),
    hatchProperties,
    entityId,
  );
}

function polygonBSPCopy(polygon: PolygonBSP): PolygonBSP {
  const { priority, vertices, plane, extendedData } = polygon;

  return polygonBSPNew(priority, vertices.slice(), planeBSPCopy(plane), extendedDataPolygonBSPCopy(extendedData));
}

function polygonSplitBSPNew(
  frontPolygon: PolygonBSP,
  backPolygon: PolygonBSP,
  splitLine: LineBSP | null,
): PolygonSplitBSP {
  return {
    frontPolygon,
    backPolygon,
    splitLine,
  };
}

export function reprojectPolygonBSP(polygon: PolygonBSP) {
  const { plane, vertices } = polygon;
  const { nX, nY, nZ, dist } = plane;

  for (let i = 0, l = vertices.length; i < l; i += 3) {
    const x = vertices[i];
    const y = vertices[i + 1];
    const z = vertices[i + 2];

    const d = nX * x + nY * y + nZ * z - dist;

    vertices[i] = x - nX * d;
    vertices[i + 1] = y - nY * d;
    vertices[i + 2] = z - nZ * d;
  }
}

export function projectVec3PlaneBSP(x: number, y: number, z: number, { nX, nY, nZ, dist }: PlaneBSP): Vec3 {
  const d = nX * x + nY * y + nZ * z - dist;

  vec3Result[0] = x - nX * d;
  vec3Result[1] = y - nY * d;
  vec3Result[2] = z - nZ * d;

  return vec3Result;
}

export function vec3PlaneBSPSide(
  x: number,
  y: number,
  z: number,
  { nX, nY, nZ, dist }: PlaneBSP,
  epsilon: number = BSP_EPSILON,
): PlaneSide {
  const d = nX * x + nY * y + nZ * z - dist;

  if (d > epsilon) {
    return PlaneSide.FRONT;
  }

  if (d < -epsilon) {
    return PlaneSide.BACK;
  }

  return PlaneSide.COPLANAR;
}

export function classifyPolygonBSP(
  plane: PlaneBSP,
  polygon: PolygonBSP,
  removingCoplanarLines: boolean = false,
  epsilon: number = BSP_EPSILON,
): PlaneSide {
  let side = PlaneSide.COPLANAR;
  let coplanarCount = 0;

  const { vertices } = polygon;

  for (let i = 0, l = vertices.length; i < l; i += 3) {
    const vertSide = vec3PlaneBSPSide(vertices[i], vertices[i + 1], vertices[i + 2], plane, epsilon);

    if (vertSide === PlaneSide.COPLANAR) {
      coplanarCount++;
    }

    if (vertSide !== side && vertSide !== PlaneSide.COPLANAR) {
      if (side === PlaneSide.COPLANAR) {
        side = vertSide;
      } else {
        return PlaneSide.INTERSECTS;
      }
    }
  }

  if (removingCoplanarLines && coplanarCount >= 2 && coplanarCount < vertices.length / 3) {
    return PlaneSide.INTERSECTS;
  }

  return side;
}

export function classifyLineBSP(
  plane: PlaneBSP,
  { x1, y1, z1, x2, y2, z2 }: LineBSP,
  epsilon: number = BSP_EPSILON,
): PlaneSide {
  const side1 = vec3PlaneBSPSide(x1, y1, z1, plane, epsilon);
  const side2 = vec3PlaneBSPSide(x2, y2, z2, plane, epsilon);

  if (side1 === side2) {
    return side1;
  } else if (side1 === PlaneSide.COPLANAR) {
    return side2;
  } else if (side2 === PlaneSide.COPLANAR) {
    return side1;
  } else {
    return PlaneSide.INTERSECTS;
  }
}

function classifySegmentBSP(
  plane: PlaneBSP,
  x1: number,
  y1: number,
  z1: number,
  x2: number,
  y2: number,
  z2: number,
  epsilon: number = BSP_EPSILON,
): PlaneSide {
  const side1 = vec3PlaneBSPSide(x1, y1, z1, plane, epsilon);
  const side2 = vec3PlaneBSPSide(x2, y2, z2, plane, epsilon);

  if (side1 === side2) {
    return side1;
  } else if (side1 === PlaneSide.COPLANAR) {
    return side2;
  } else if (side2 === PlaneSide.COPLANAR) {
    return side1;
  } else {
    return PlaneSide.INTERSECTS;
  }
}

function splitVisibleLinesAndNormals(
  plane: PlaneBSP,
  linesToSplit: LineBSP[],
  frontLines: LineBSP[],
  backLines: LineBSP[],
  neighborNormals: Vec3[],
  frontNeighborNormals: Vec3[],
  backNeighborNormals: Vec3[],
  coplanarsToFront?: boolean,
) {
  const neighborCount = neighborNormals.length;

  if (neighborCount > 0) {
    if (!(neighborCount === linesToSplit.length)) {
      console.assert(false);
    }
  }

  for (let i = 0, l = linesToSplit.length; i < l; i++) {
    const { x1, y1, z1, x2, y2, z2 } = linesToSplit[i];
    const neighborNormal = neighborNormals[i];

    const neighborNormalX = neighborNormal ? neighborNormal[0] : 0;
    const neighborNormalY = neighborNormal ? neighborNormal[1] : 0;
    const neighborNormalZ = neighborNormal ? neighborNormal[2] : 0;

    const side1 = vec3PlaneBSPSide(x1, y1, z1, plane);
    const side2 = vec3PlaneBSPSide(x2, y2, z2, plane);

    if (side1 === side2 || side1 === PlaneSide.COPLANAR || side2 === PlaneSide.COPLANAR) {
      if (side1 === PlaneSide.COPLANAR && side2 === PlaneSide.COPLANAR) {
        if (coplanarsToFront !== undefined) {
          if (coplanarsToFront) {
            frontLines.push(lineBSPNew(x1, y1, z1, x2, y2, z2));
          } else {
            backLines.push(lineBSPNew(x1, y1, z1, x2, y2, z2));
          }

          if (neighborCount > 0) {
            if (coplanarsToFront) {
              frontNeighborNormals.push([neighborNormalX, neighborNormalY, neighborNormalZ]);
            } else {
              backNeighborNormals.push([neighborNormalX, neighborNormalY, neighborNormalZ]);
            }
          }
        } else {
          frontLines.push(lineBSPNew(x1, y1, z1, x2, y2, z2));
          backLines.push(lineBSPNew(x1, y1, z1, x2, y2, z2));

          if (neighborCount > 0) {
            frontNeighborNormals.push([neighborNormalX, neighborNormalY, neighborNormalZ]);
            backNeighborNormals.push([neighborNormalX, neighborNormalY, neighborNormalZ]);
          }
        }
      } else if (side1 === PlaneSide.FRONT || side2 === PlaneSide.FRONT) {
        frontLines.push(lineBSPNew(x1, y1, z1, x2, y2, z2));

        if (neighborCount > 0) {
          frontNeighborNormals.push([neighborNormalX, neighborNormalY, neighborNormalZ]);
        }
      } else {
        backLines.push(lineBSPNew(x1, y1, z1, x2, y2, z2));

        if (neighborCount > 0) {
          backNeighborNormals.push([neighborNormalX, neighborNormalY, neighborNormalZ]);
        }
      }
    } else {
      /**
       * intersectionLinePlaneBSP can return null when no intersection is detected,
       * but we already know that the intersection is occurring as at this point in the code
       * because the segment points are living on different sides of the plane.
       * Adding the emptyLinePlaneBSPResult here to satisfy the strict null check.
       *
       * This situation is repeated in more places in this file.
       */
      const {
        x: xI,
        y: yI,
        z: zI,
      } = intersectionLinePlaneBSP(plane, x1, y1, z1, x2, y2, z2) ?? emptyLinePlaneBSPResult;

      if (side1 === PlaneSide.FRONT) {
        frontLines.push(lineBSPNew(x1, y1, z1, xI, yI, zI));
        backLines.push(lineBSPNew(xI, yI, zI, x2, y2, z2));
      } else {
        frontLines.push(lineBSPNew(xI, yI, zI, x2, y2, z2));
        backLines.push(lineBSPNew(x1, y1, z1, xI, yI, zI));
      }

      if (neighborCount > 0) {
        frontNeighborNormals.push([neighborNormalX, neighborNormalY, neighborNormalZ]);
        backNeighborNormals.push([neighborNormalX, neighborNormalY, neighborNormalZ]);
      }
    }
  }
}

function splitVisibleLines(
  plane: PlaneBSP,
  linesToSplit: LineBSP[],
  frontLines: LineBSP[],
  backLines: LineBSP[],
  coplanarsToFront?: boolean,
) {
  for (let i = 0, l = linesToSplit.length; i < l; i++) {
    const { x1, y1, z1, x2, y2, z2 } = linesToSplit[i];

    const side1 = vec3PlaneBSPSide(x1, y1, z1, plane);
    const side2 = vec3PlaneBSPSide(x2, y2, z2, plane);

    if (side1 === side2 || side1 === PlaneSide.COPLANAR || side2 === PlaneSide.COPLANAR) {
      if (side1 === PlaneSide.COPLANAR && side2 === PlaneSide.COPLANAR) {
        if (coplanarsToFront !== undefined) {
          if (coplanarsToFront) {
            frontLines.push(lineBSPNew(x1, y1, z1, x2, y2, z2));
          } else {
            backLines.push(lineBSPNew(x1, y1, z1, x2, y2, z2));
          }
        } else {
          frontLines.push(lineBSPNew(x1, y1, z1, x2, y2, z2));
          backLines.push(lineBSPNew(x1, y1, z1, x2, y2, z2));
        }
      } else if (side1 === PlaneSide.FRONT || side2 === PlaneSide.FRONT) {
        frontLines.push(lineBSPNew(x1, y1, z1, x2, y2, z2));
      } else {
        backLines.push(lineBSPNew(x1, y1, z1, x2, y2, z2));
      }
    } else {
      const {
        x: xI,
        y: yI,
        z: zI,
      } = intersectionLinePlaneBSP(plane, x1, y1, z1, x2, y2, z2) ?? emptyLinePlaneBSPResult;

      if (side1 === PlaneSide.FRONT) {
        frontLines.push(lineBSPNew(x1, y1, z1, xI, yI, zI));
        backLines.push(lineBSPNew(xI, yI, zI, x2, y2, z2));
      } else {
        frontLines.push(lineBSPNew(xI, yI, zI, x2, y2, z2));
        backLines.push(lineBSPNew(x1, y1, z1, xI, yI, zI));
      }
    }
  }
}

const emptyPolygonBSP = polygonBSPNew(0, [], planeBSPNew(0, 0, 0, 0));
const polygonSplitBSPResult = polygonSplitBSPNew(emptyPolygonBSP, emptyPolygonBSP, null);

function splitExtendedPolygonPlaneBSP(
  plane: PlaneBSP,
  polygon: PolygonBSP,
  extendedData: ExtendedDataPolygonBSP,
  epsilon: number = BSP_EPSILON,
): PolygonSplitBSP {
  const frontVertices: number[] = [];
  const backVertices: number[] = [];
  const frontVisibleLines: LineBSP[] = [];
  const backVisibleLines: LineBSP[] = [];
  const frontNeighborNormals: Vec3[] = [];
  const backNeighborNormals: Vec3[] = [];
  const frontSplitLines: LineBSP[] = [];
  const backSplitLines: LineBSP[] = [];
  const frontHatchLines: LineBSP[] = [];
  const backHatchLines: LineBSP[] = [];

  const frontEdgePlanes: PlaneBSP[] = [];
  const backEdgePlanes: PlaneBSP[] = [];

  let lastFrontEdgePlane: PlaneBSP | null = null;
  let lastBackEdgePlane: PlaneBSP | null = null;

  const addFrontEdgePlane = (edgePlane: PlaneBSP) => {
    if (lastFrontEdgePlane === null) {
      lastFrontEdgePlane = edgePlane;
    } else {
      frontEdgePlanes.push(edgePlane);
    }
  };

  const addBackEdgePlane = (edgePlane: PlaneBSP) => {
    if (lastBackEdgePlane === null) {
      lastBackEdgePlane = edgePlane;
    } else {
      backEdgePlanes.push(edgePlane);
    }
  };

  const fixEdgePlanes = (edgePlanes: PlaneBSP[], lastEdgePlane: PlaneBSP | null) => {
    if (lastEdgePlane) {
      edgePlanes.push(lastEdgePlane);
    }
  };

  const { priority, vertices, plane: polygonPlane } = polygon;
  const { visibleLines, splitLines, hatchLines, neighborNormals } = extendedData;

  const flipPlane = planeBSPFlip(plane);

  splitVisibleLinesAndNormals(
    plane,
    visibleLines,
    frontVisibleLines,
    backVisibleLines,
    neighborNormals,
    frontNeighborNormals,
    backNeighborNormals,
    true,
  );
  splitVisibleLines(plane, splitLines, frontSplitLines, backSplitLines, true);
  splitVisibleLines(plane, hatchLines, frontHatchLines, backHatchLines);

  const edgePlanes = extendedData.edgePlanes;
  const edgePlanesCount = edgePlanes?.length ?? 0;

  let ax = vertices[0];
  let ay = vertices[1];
  let az = vertices[2];
  let sideA = vec3PlaneBSPSide(ax, ay, az, plane, epsilon);

  const splitLine: number[] = [];
  for (let i = 0, e = 0, l = vertices.length; i < l; i += 3, e++) {
    const nextI = (i + 3) % l;
    const edgePlane = e < edgePlanesCount ? edgePlanes[e] : null;

    const bx = vertices[nextI];
    const by = vertices[nextI + 1];
    const bz = vertices[nextI + 2];

    const sideB = vec3PlaneBSPSide(bx, by, bz, plane, epsilon);

    if (sideA === PlaneSide.FRONT && sideB === PlaneSide.FRONT) {
      pushXYZ(frontVertices, bx, by, bz);

      if (edgePlane) {
        addFrontEdgePlane(edgePlane);
      }
    } else if (sideA === PlaneSide.COPLANAR && sideB === PlaneSide.FRONT) {
      pushXYZ(frontVertices, bx, by, bz);

      if (edgePlane) {
        addFrontEdgePlane(edgePlane);
      }
    } else if (sideA === PlaneSide.BACK && sideB === PlaneSide.FRONT) {
      const {
        x: ix,
        y: iy,
        z: iz,
      } = intersectionLinePlaneBSP(plane, bx, by, bz, ax, ay, az, epsilon) ?? emptyLinePlaneBSPResult;

      pushXYZXYZ(frontVertices, ix, iy, iz, bx, by, bz);
      pushXYZ(backVertices, ix, iy, iz);
      pushXYZ(splitLine, ix, iy, iz);

      if (edgePlane) {
        addFrontEdgePlane(flipPlane);
        addFrontEdgePlane(edgePlane);
        addBackEdgePlane(edgePlane);
      }
    } else if (sideA === PlaneSide.FRONT && sideB === PlaneSide.COPLANAR) {
      pushXYZ(frontVertices, bx, by, bz);
      pushXYZ(splitLine, bx, by, bz);

      if (edgePlane) {
        addFrontEdgePlane(edgePlane);
      }
    } else if (sideA === PlaneSide.COPLANAR && sideB === PlaneSide.COPLANAR) {
      pushXYZ(frontVertices, bx, by, bz);
      if (edgePlane) {
        addFrontEdgePlane(edgePlane);
      }
    } else if (sideA === PlaneSide.BACK && sideB === PlaneSide.COPLANAR) {
      pushXYZ(frontVertices, bx, by, bz);
      pushXYZ(backVertices, bx, by, bz);
      pushXYZ(splitLine, bx, by, bz);

      if (edgePlane) {
        addFrontEdgePlane(flipPlane);
        addBackEdgePlane(edgePlane);
      }
    } else if (sideA === PlaneSide.FRONT && sideB === PlaneSide.BACK) {
      const {
        x: ix,
        y: iy,
        z: iz,
      } = intersectionLinePlaneBSP(plane, ax, ay, az, bx, by, bz, epsilon) ?? emptyLinePlaneBSPResult;

      pushXYZ(frontVertices, ix, iy, iz);
      pushXYZXYZ(backVertices, ix, iy, iz, bx, by, bz);
      pushXYZ(splitLine, ix, iy, iz);

      if (edgePlane) {
        addFrontEdgePlane(edgePlane);
        addBackEdgePlane(plane);
        addBackEdgePlane(edgePlane);
      }
    } else if (sideA === PlaneSide.COPLANAR && sideB === PlaneSide.BACK) {
      pushXYZXYZ(backVertices, ax, ay, az, bx, by, bz);

      if (edgePlane) {
        addBackEdgePlane(plane);
        addBackEdgePlane(edgePlane);
      }
    } else {
      pushXYZ(backVertices, bx, by, bz);

      if (edgePlane) {
        addBackEdgePlane(edgePlane);
      }
    }

    ax = bx;
    ay = by;
    az = bz;
    sideA = sideB;
  }

  fixEdgePlanes(frontEdgePlanes, lastFrontEdgePlane);
  fixEdgePlanes(backEdgePlanes, lastBackEdgePlane);

  const { id, hatchProperties, entityId } = extendedData;

  const frontExtendedData = extendedDataPolygonBSPNew(
    id,
    getVerticesAABB3(frontVertices),
    frontVisibleLines,
    frontSplitLines,
    frontHatchLines,
    frontNeighborNormals,
    frontEdgePlanes,
    hatchProperties,
    entityId,
  );

  const backExtendedData = extendedDataPolygonBSPNew(
    id,
    getVerticesAABB3(backVertices),
    backVisibleLines,
    backSplitLines,
    backHatchLines,
    backNeighborNormals,
    backEdgePlanes,
    hatchProperties,
    entityId,
  );

  polygonSplitBSPResult.frontPolygon = polygonBSPNew(priority, frontVertices, polygonPlane, frontExtendedData);
  polygonSplitBSPResult.backPolygon = polygonBSPNew(priority, backVertices, polygonPlane, backExtendedData);
  polygonSplitBSPResult.splitLine =
    splitLine.length > 0
      ? lineBSPNew(splitLine[0], splitLine[1], splitLine[2], splitLine[3], splitLine[4], splitLine[5])
      : null;

  return polygonSplitBSPResult;
}

function splitSimplePolygonPlaneBSP(
  plane: PlaneBSP,
  polygon: PolygonBSP,
  epsilon: number = BSP_EPSILON,
): PolygonSplitBSP {
  const frontVertices: number[] = [];
  const backVertices: number[] = [];

  const { priority, vertices, plane: polygonPlane } = polygon;

  let ax = vertices[0];
  let ay = vertices[1];
  let az = vertices[2];
  let sideA = vec3PlaneBSPSide(ax, ay, az, plane, epsilon);

  const splitLine: number[] = [];
  for (let i = 0, l = vertices.length; i < l; i += 3) {
    const nextI = (i + 3) % l;

    const bx = vertices[nextI];
    const by = vertices[nextI + 1];
    const bz = vertices[nextI + 2];

    const sideB = vec3PlaneBSPSide(bx, by, bz, plane, epsilon);

    if (sideA === PlaneSide.FRONT && sideB === PlaneSide.FRONT) {
      pushXYZ(frontVertices, bx, by, bz);
    } else if (sideA === PlaneSide.COPLANAR && sideB === PlaneSide.FRONT) {
      pushXYZ(frontVertices, bx, by, bz);
    } else if (sideA === PlaneSide.BACK && sideB === PlaneSide.FRONT) {
      const {
        x: ix,
        y: iy,
        z: iz,
      } = intersectionLinePlaneBSP(plane, bx, by, bz, ax, ay, az, epsilon) ?? emptyLinePlaneBSPResult;

      pushXYZXYZ(frontVertices, ix, iy, iz, bx, by, bz);
      pushXYZ(backVertices, ix, iy, iz);
      pushXYZ(splitLine, ix, iy, iz);
    } else if (sideA === PlaneSide.FRONT && sideB === PlaneSide.COPLANAR) {
      pushXYZ(frontVertices, bx, by, bz);
      pushXYZ(splitLine, bx, by, bz);
    } else if (sideA === PlaneSide.COPLANAR && sideB === PlaneSide.COPLANAR) {
      pushXYZ(frontVertices, bx, by, bz);
    } else if (sideA === PlaneSide.BACK && sideB === PlaneSide.COPLANAR) {
      pushXYZ(frontVertices, bx, by, bz);
      pushXYZ(backVertices, bx, by, bz);
      pushXYZ(splitLine, bx, by, bz);
    } else if (sideA === PlaneSide.FRONT && sideB === PlaneSide.BACK) {
      const {
        x: ix,
        y: iy,
        z: iz,
      } = intersectionLinePlaneBSP(plane, ax, ay, az, bx, by, bz, epsilon) ?? emptyLinePlaneBSPResult;

      pushXYZ(frontVertices, ix, iy, iz);
      pushXYZXYZ(backVertices, ix, iy, iz, bx, by, bz);
      pushXYZ(splitLine, ix, iy, iz);
    } else if (sideA === PlaneSide.COPLANAR && sideB === PlaneSide.BACK) {
      pushXYZXYZ(backVertices, ax, ay, az, bx, by, bz);
    } else {
      pushXYZ(backVertices, bx, by, bz);
    }

    ax = bx;
    ay = by;
    az = bz;
    sideA = sideB;
  }

  polygonSplitBSPResult.frontPolygon = polygonBSPNew(priority, frontVertices, polygonPlane);
  polygonSplitBSPResult.backPolygon = polygonBSPNew(priority, backVertices, polygonPlane);
  polygonSplitBSPResult.splitLine =
    splitLine.length > 0
      ? lineBSPNew(splitLine[0], splitLine[1], splitLine[2], splitLine[3], splitLine[4], splitLine[5])
      : null;

  return polygonSplitBSPResult;
}

export function splitPolygonPlaneBSP(
  plane: PlaneBSP,
  polygon: PolygonBSP,
  epsilon: number = BSP_EPSILON,
): PolygonSplitBSP {
  const { extendedData } = polygon;
  if (extendedData) {
    return splitExtendedPolygonPlaneBSP(plane, polygon, extendedData, epsilon);
  } else {
    return splitSimplePolygonPlaneBSP(plane, polygon, epsilon);
  }
}

export function buildEdgePlane(
  x1: number,
  y1: number,
  z1: number,
  x2: number,
  y2: number,
  z2: number,
  nX: number,
  nY: number,
  nZ: number,
): PlaneBSP | null {
  return planeBSPByThreePoints(x1, y1, z1, x2, y2, z2, x2 + nX, y2 + nY, z2 + nZ);
}

export function buildEdgePolygon(
  x1: number,
  y1: number,
  z1: number,
  x2: number,
  y2: number,
  z2: number,
  nX: number,
  nY: number,
  nZ: number,
): PolygonBSP | null {
  const x3 = x2 + nX;
  const y3 = y2 + nY;
  const z3 = z2 + nZ;

  const x4 = x1 + nX;
  const y4 = y1 + nY;
  const z4 = z1 + nZ;

  return buildPolygonByFourPoints(x1, y1, z1, x2, y2, z2, x3, y3, z3, x4, y4, z4);
}

export function buildPolygonByFourPoints(
  x1: number,
  y1: number,
  z1: number,
  x2: number,
  y2: number,
  z2: number,
  x3: number,
  y3: number,
  z3: number,
  x4: number,
  y4: number,
  z4: number,
  emitAssert: boolean = true,
): PolygonBSP | null {
  const plane = planeBSPByThreePoints(x1, y1, z1, x2, y2, z2, x3, y3, z3);

  if (!plane) {
    return null;
  }

  const side = vec3PlaneBSPSide(x4, y4, z4, plane, BSP_EPSILON);
  if (emitAssert) {
    if (!(side === PlaneSide.COPLANAR)) {
      console.assert(false, "Non planar face in buildPolygonByFourPoints");
    }
  } else if (side !== PlaneSide.COPLANAR) {
    return null;
  }

  return polygonBSPNew(0, [x1, y1, z1, x2, y2, z2, x3, y3, z3, x4, y4, z4], plane);
}

export function buildPerimeterPolygons(pl: PolyLine2, plane: Plane, height: number = 1): PolygonBSP[] {
  const [nX, nY, nZ] = planeNormal(plane);
  const pl3 = polyLine2OnPlane(plane, pl);

  const polygons: PolygonBSP[] = [];
  for (let i = 0, l = pl3.length - 3; i < l; i += 3) {
    const edgePolygon = buildEdgePolygon(
      pl3[i],
      pl3[i + 1],
      pl3[i + 2],
      pl3[i + 3],
      pl3[i + 4],
      pl3[i + 5],
      nX * height,
      nY * height,
      nZ * height,
    );

    if (edgePolygon) {
      polygons.push(edgePolygon);
    }
  }

  return polygons;
}

export function buildPerimeterBSP(pl: PolyLine2, plane: Plane, epsilon: number = BSP_EPSILON): NodeBSP | null {
  const polygons = buildPerimeterPolygons(pl, plane);

  return buildBSPNode({ polygons, epsilon });
}

export function buildPolygonsFromPolyline2(
  polyline2: PolyLine2,
  plane: Plane,
  priority: number = 0,
  polygons: PolygonBSP[] = [],
): PolygonBSP[] {
  const face = faceNew([polyline2], plane);

  return buildPolygonsFromFaces({
    faces: [face],
    priority,
    polygons,
  });
}

function buildFaceBSP(face: Face, visibleLines: LineBSP[] | null = null): NodeBSP | null {
  const { loops, plane } = face;
  const exteriorVertices = polyLine3RemoveDuplicateNeighbors(polyLine2OnPlane(plane, loops[0]));

  if (exteriorVertices.length === 0) {
    return null;
  }

  const [nX, nY, nZ] = planeNormal(plane);

  const edgePolygons: PolygonBSP[] = [];

  for (let l = exteriorVertices.length - 3, i = l - 3; i >= 0; i -= 3) {
    const nextI = i >= 3 ? i - 3 : l - 3;

    const x1 = exteriorVertices[i];
    const y1 = exteriorVertices[i + 1];
    const z1 = exteriorVertices[i + 2];

    const x2 = exteriorVertices[nextI];
    const y2 = exteriorVertices[nextI + 1];
    const z2 = exteriorVertices[nextI + 2];

    if (visibleLines) {
      visibleLines.push(lineBSPNew(x1, y1, z1, x2, y2, z2));
    }

    const edgePolygon = buildEdgePolygon(x1, y1, z1, x2, y2, z2, -nX, -nY, -nZ);

    if (edgePolygon) {
      edgePolygons.push(edgePolygon);
    }
  }

  // this loop starts with the counter set to 1 to use only the inner loops
  for (let loopIndex = 1, loopCount = loops.length; loopIndex < loopCount; loopIndex++) {
    const holePoints3 = polyLine3RemoveDuplicateNeighbors(polyLine2OnPlane(plane, polyLine2SetCCW(loops[loopIndex])));

    for (let l = exteriorVertices.length - 3, i = l - 3; i >= 0; i -= 3) {
      const nextI = i >= 3 ? i - 3 : l - 3;

      const ax = holePoints3[i];
      const ay = holePoints3[i + 1];
      const az = holePoints3[i + 2];

      const bx = holePoints3[nextI];
      const by = holePoints3[nextI + 1];
      const bz = holePoints3[nextI + 2];

      if (visibleLines) {
        visibleLines.push(lineBSPNew(ax, ay, az, bx, by, bz));
      }

      const edgePolygon = buildEdgePolygon(ax, ay, az, bx, by, bz, -nX, -nY, -nZ);

      if (edgePolygon) {
        edgePolygons.push(edgePolygon);
      }
    }
  }

  return addPolygonsToBSP(null, null, edgePolygons);
}

export function buildPolygonEdgePlanes(vertices: number[], nX: number, nY: number, nZ: number): PlaneBSP[] {
  const edgePlanes: PlaneBSP[] = [];
  for (let i = 0, l = vertices.length, e = 0; i < l; i += 3, e++) {
    const nextI = (i + 3) % l;

    const x1 = vertices[i];
    const y1 = vertices[i + 1];
    const z1 = vertices[i + 2];

    const x2 = vertices[nextI];
    const y2 = vertices[nextI + 1];
    const z2 = vertices[nextI + 2];

    const edgePlane = buildEdgePlane(x1, y1, z1, x2, y2, z2, nX, nY, nZ);

    if (edgePlane) {
      edgePlanes.push(edgePlane);
    }
  }

  return edgePlanes;
}

interface BuildPolygonsFromFacesExtendedOptions {
  designMaterialId?: MaterialSrc;
  hatchMaterial?: HatchMaterial;
  generateNeighbors?: boolean;
  uvGen?: UVGeneration;
  entityId?: string;
  materialTransformation?: UVTransformation | UVTransformation[]; // Mirrored building MUST be reflected on this transformation
}

interface BuildPolygonsFromFacesOptions {
  faces: (Face | null)[];
  priority?: number;
  polygons?: PolygonBSP[];
  extendedOptions?: BuildPolygonsFromFacesExtendedOptions;
  ignoreFastPath?: boolean;
  offsetZ?: number;
  normalOffset?: number;
}

export function buildPolygonsFromFaces({
  faces,
  priority = 0,
  polygons = [],
  extendedOptions,
  ignoreFastPath = false,
  offsetZ = 0,
  normalOffset = 0,
}: BuildPolygonsFromFacesOptions): PolygonBSP[] {
  const startIndex = polygons.length;
  let id = polygons.length;

  for (let faceIndex = 0, faceCount = faces.length; faceIndex < faceCount; faceIndex++) {
    const face = faces[faceIndex];
    if (!face) {
      continue;
    }

    const { loops } = face;
    let { plane } = face;

    if (planeNaN(plane) || loops.length === 0 || polyLine2Area(loops[0]) < DIST_EPSILON_SQUARED) {
      continue;
    }

    plane = planeCopy(plane);

    if (Math.abs(offsetZ) > DIST_EPSILON) {
      planeMove(plane, 0, 0, offsetZ);
    }

    if (Math.abs(normalOffset) > DIST_EPSILON) {
      planeMoveAlongNormal(plane, normalOffset);
    }

    const [nX, nY, nZ] = planeNormal(plane);
    const [oX, oY, oZ] = planeEval(plane, loops[0][0], loops[0][1]);
    const polygonPlane = planeBSPByPointAndNormal(oX, oY, oZ, nX, nY, nZ);

    if (
      !ignoreFastPath &&
      !extendedOptions &&
      face.loops.length === 1 &&
      polyLine2IsConvex(loops[0]) &&
      polyLine2Area(loops[0]) > AREA_TOLERANCE
    ) {
      const exteriorVertices = polyLine3RemoveDuplicateNeighbors(polyLine2OnPlane(plane, loops[0]));

      if (exteriorVertices.length === 0) {
        continue;
      }

      const vertices = new Array<number>(exteriorVertices.length - 3);
      for (let i = 0, l = exteriorVertices.length - 3; i < l; i += 3) {
        vertices[i] = exteriorVertices[i];
        vertices[i + 1] = exteriorVertices[i + 1];
        vertices[i + 2] = exteriorVertices[i + 2];
      }

      const convexPolygon = polygonBSPNew(priority, vertices, polygonPlane);

      polygons.push(convexPolygon);

      continue;
    }

    const visibleLines: LineBSP[] = [];
    const root = buildFaceBSP(face, visibleLines);
    if (!root) {
      return [];
    }

    const aabb2 = polyLine2AABB2(loops[0]);
    const vertices = new Array<number>(12);

    [vertices[0], vertices[1], vertices[2]] = planeEval(plane, aabb2.minX, aabb2.minY);
    [vertices[3], vertices[4], vertices[5]] = planeEval(plane, aabb2.maxX, aabb2.minY);
    [vertices[6], vertices[7], vertices[8]] = planeEval(plane, aabb2.maxX, aabb2.maxY);
    [vertices[9], vertices[10], vertices[11]] = planeEval(plane, aabb2.minX, aabb2.maxY);

    let extendedData: ExtendedDataPolygonBSP | undefined = undefined;
    if (extendedOptions) {
      const edgePlanes = buildPolygonEdgePlanes(vertices, nX, nY, nZ);
      if (edgePlanes.length < 4) {
        continue;
      }

      const { hatchMaterial, designMaterialId, uvGen, entityId, materialTransformation } = extendedOptions;
      const designMaterialKey = designMaterialId ? materialCacheKey(designMaterialId) : undefined;

      let hatchLines: LineBSP[] = [];

      if (hatchMaterial) {
        let xform: UVTransformation | undefined;
        if (materialTransformation) {
          if (!(!Array.isArray(materialTransformation) || materialTransformation.length === faceCount)) {
            console.assert(false);
          }
          xform = Array.isArray(materialTransformation) ? materialTransformation[faceIndex] : materialTransformation;
        }

        const { uvPlane, aabb2 } = getHatchAABB2({ plane, loop: loops[0], uvGen, materialTransformation: xform });
        hatchLines = fillHatchLines(hatchMaterial, uvPlane, plane, aabb2);
      }

      extendedData = extendedDataPolygonBSPNew(
        id,
        getVerticesAABB3(vertices),
        visibleLines,
        [],
        hatchLines,
        [],
        edgePlanes,
        hatchPropertiesNew(plane, uvGen, designMaterialId, designMaterialKey, hatchMaterial),
        entityId,
      );
    }

    const polygon = polygonBSPNew(priority, vertices, polygonPlane, extendedData);

    const convexPolygons: PolygonBSP[] = [];
    splitPolygonBSP({
      node: root,
      polygon,
      coplanarsToFront: false,
      frontPolygons: convexPolygons,
    });

    for (let p = 0, pl = convexPolygons.length; p < pl; p++) {
      const convexPolygon = convexPolygons[p];
      if (isDegeneratePolygonBSP(convexPolygon)) {
        continue;
      }

      const { extendedData: convexPolygonExtendedData } = convexPolygon;
      if (convexPolygonExtendedData) {
        convexPolygonExtendedData.id = id++;
        convexPolygonExtendedData.hatchLines = clipLinesOutsideConvexVol(
          convexPolygonExtendedData.hatchLines,
          convexPolygonExtendedData.edgePlanes,
          false,
        );
      }

      reprojectPolygonBSP(convexPolygon);

      polygons.push(convexPolygon);
    }
  }

  if (extendedOptions && extendedOptions.generateNeighbors) {
    for (let ia = startIndex, l = polygons.length; ia < l; ++ia) {
      const neighborNormals: Vec3[] = [];
      const polygonA = polygons[ia];
      const edgesA = polygonA.extendedData?.visibleLines;
      if (!edgesA) {
        continue;
      }
      const edgeCountA = edgesA.length;
      if (edgeCountA > 4) {
        // Optimization: Assume that complex polygons will have only sharp edges.
        continue;
      }
      for (let edgeA = 0; edgeA < edgeCountA; ++edgeA) {
        const xa1 = edgesA[edgeA].x1;
        const ya1 = edgesA[edgeA].y1;
        const za1 = edgesA[edgeA].z1;
        const xa2 = edgesA[edgeA].x2;
        const ya2 = edgesA[edgeA].y2;
        const za2 = edgesA[edgeA].z2;
        let found = false;
        for (let ib = startIndex; ib < l && !found; ++ib) {
          if (ib === ia) {
            continue;
          }
          const polygonB = polygons[ib];
          const edgesB = polygonB.extendedData?.visibleLines;
          if (!edgesB) {
            continue;
          }
          // Find neighboring polygon for this edge, and record its normal
          for (let edgeB = 0, edgeCountB = edgesB.length; edgeB < edgeCountB; edgeB++) {
            const xb1 = edgesB[edgeB].x1;
            const yb1 = edgesB[edgeB].y1;
            const zb1 = edgesB[edgeB].z1;
            const xb2 = edgesB[edgeB].x2;
            const yb2 = edgesB[edgeB].y2;
            const zb2 = edgesB[edgeB].z2;

            const edgeFound1 =
              vec3Dist(xa1, ya1, za1, xb1, yb1, zb1) <= DIST_EPSILON &&
              vec3Dist(xa2, ya2, za2, xb2, yb2, zb2) <= DIST_EPSILON;

            const edgeFound2 =
              vec3Dist(xa1, ya1, za1, xb2, yb2, zb2) <= DIST_EPSILON &&
              vec3Dist(xa2, ya2, za2, xb1, yb1, zb1) <= DIST_EPSILON;

            if (edgeFound1 || edgeFound2) {
              found = true;
              neighborNormals.push([polygonB.plane.nX, polygonB.plane.nY, polygonB.plane.nZ]);
            }
          }
        }
      }
      if (neighborNormals.length === edgeCountA && polygonA.extendedData) {
        polygonA.extendedData.neighborNormals = neighborNormals;
      }
    }
  }

  return polygons;
}

export function selectFirstPlaneBSP(polygons: PolygonBSP[]): PlaneBSP | null {
  polygons = polygons.filter((p) => !!p); // remove nulls
  if (polygons.length > 0) {
    const { plane } = polygons[0];

    return plane;
  } else {
    return null;
  }
}

export function selectBestPlaneBSP(polygons: PolygonBSP[]): PlaneBSP | null {
  polygons = polygons.filter((p) => !!p); // remove nulls
  const polygonCount = polygons.length;

  let bestPlane: PlaneBSP | null = null;
  const bestSplitCount = polygons.length;

  for (let i = 0; i < polygonCount; i++) {
    const { plane } = polygons[i];

    let splitCount = 0;

    for (let j = 0; j < polygonCount; j++) {
      const polygonSide = classifyPolygonBSP(plane, polygons[j]);

      if (polygonSide === PlaneSide.INTERSECTS) {
        splitCount++;
      }
    }

    if (splitCount < bestSplitCount) {
      bestPlane = plane;
      splitCount = bestSplitCount;
    }
  }

  return bestPlane;
}

export function isDegeneratedFace(face: Face, tolerance: number = BSP_EPSILON): boolean {
  const { loops } = face;
  if (loops.length === 0 || polyLine2Area(loops[0]) < DIST_EPSILON_SQUARED) {
    return true;
  }

  // remove thin faces
  const simplifiedLoop = polyLine2CopySimplify(loops[0], tolerance);
  if (polyLine2EdgeCount(simplifiedLoop) < 3) {
    return true;
  }

  const aabb = polyLine2AABB2(simplifiedLoop);
  const w = aabb2Width(aabb);
  const h = aabb2Height(aabb);
  const minDist = 2 * tolerance;

  if (w < minDist || h < minDist) {
    return true;
  }

  const minDistSq = minDist * minDist;
  for (let i = 0, l = simplifiedLoop.length - 2; i < l; i += 2) {
    // current edge
    const startX = simplifiedLoop[i];
    const startY = simplifiedLoop[i + 1];
    const endX = simplifiedLoop[i + 2];
    const endY = simplifiedLoop[i + 3];

    const [dirX, dirY] = vec2Normalize(endX - startX, endY - startY);

    // next edge end
    const x = simplifiedLoop[(i + 4) % l];
    const y = simplifiedLoop[(i + 5) % l];

    // look for degenerate edges
    const [px, py] = ray2Project(startX, startY, dirX, dirY, x, y);
    const distSq = vec2DistSquared(x, y, px, py);
    if (distSq < minDistSq) {
      return true;
    }
  }

  return false;
}

export function isValidFaceForBSPVolume(face: Face, thickness: number, tolerance: number = BSP_EPSILON): boolean {
  return thickness > tolerance && !isDegeneratedFace(face, tolerance);
}

export function copyBSP(node: NodeBSP | null): NodeBSP | null {
  return copyBSPExt(node, null);
}

function copyBSPExt(node: NodeBSP | null, parent: NodeBSP | null): NodeBSP | null {
  if (!node) {
    return null;
  }

  const { plane, frontNode, backNode, userData } = node;

  return nodeBSPNew(
    planeBSPCopy(plane),
    node.polygons.map(polygonBSPCopy),
    parent,
    copyBSPExt(frontNode, node),
    copyBSPExt(backNode, node),
    userData,
  );
}

export function getVerticesAABB3(vertices: number[]): AABB3 {
  return polyLine3AABB3(vertices);
}

export function getNodeAABB3(node: NodeBSP | null): AABB3 {
  let aabb3 = aabb3New(
    Number.POSITIVE_INFINITY,
    Number.POSITIVE_INFINITY,
    Number.POSITIVE_INFINITY,
    Number.NEGATIVE_INFINITY,
    Number.NEGATIVE_INFINITY,
    Number.NEGATIVE_INFINITY,
  );

  if (!node) {
    return aabb3;
  }

  const { polygons, frontNode, backNode } = node;
  for (let i = 0, l = polygons.length; i < l; i++) {
    const { vertices } = polygons[i];
    const verticesAABB3 = getVerticesAABB3(vertices);
    aabb3 = aabb3Union(aabb3, verticesAABB3);
  }

  const addNodeAabb3 = (childNode: NodeBSP | null) => {
    const childAabb3 = getNodeAABB3(childNode);
    aabb3 = aabb3Union(aabb3, childAabb3);
  };

  addNodeAabb3(frontNode);
  addNodeAabb3(backNode);

  return aabb3;
}

function planeBSPDot(a: PlaneBSP, b: PlaneBSP) {
  return a.nX * b.nX + a.nY * b.nY + a.nZ * b.nZ;
}

export function buildBSPNodeFromPolyLine2(pl: PolyLine2): NodeBSP | null {
  const outlinePolygons: PolygonBSP[] = [];
  for (let i = 0, l = polyLine2EdgeCount(pl); i < l; ++i) {
    const [ax, ay] = polyLine2EdgeStart(pl, i);
    const [bx, by] = polyLine2EdgeEnd(pl, i);

    const edgePolygon = buildEdgePolygon(ax, ay, 0, bx, by, 0, 0, 0, 1);

    if (edgePolygon) {
      outlinePolygons.push(edgePolygon);
    }
  }

  return buildBSPNode({ polygons: outlinePolygons });
}

export function buildBSPNodeFromAABB3(aabb3: AABB3): NodeBSP | null {
  const { minX, minY, minZ, maxX, maxY, maxZ } = aabb3;
  const planes = [
    planeBSPByPointAndNormal(minX, 0, 0, -1, 0, 0),
    planeBSPByPointAndNormal(maxX, 0, 0, 1, 0, 0),
    planeBSPByPointAndNormal(0, minY, 0, 0, -1, 0),
    planeBSPByPointAndNormal(0, maxY, 0, 0, 1, 0),
    planeBSPByPointAndNormal(0, 0, minZ, 0, 0, -1),
    planeBSPByPointAndNormal(0, 0, maxZ, 0, 0, 1),
  ];

  return addConvexPlanesToBSPLeaf(planes);
}

interface BuildBSPNodeOptions {
  readonly polygons: PolygonBSP[];
  readonly epsilon?: number;
  readonly userData?: any;
  readonly maxDepth?: number;
  readonly maxDepthErrorHandle?: MaxBSPDepthErrorHandler;
}

interface BuildBSPNodeStackEntry {
  readonly parentNode: NodeBSP | null;
  readonly polygons: PolygonBSP[];
  readonly depth: number;
  readonly isFrontNode: boolean;
}

export function buildBSPNode({
  polygons,
  epsilon = BSP_EPSILON,
  userData,
  maxDepth = MAX_BSP_DEPTH,
  maxDepthErrorHandle = () => {
    console.warn("buildBSPNode max depth reached");
  },
}: BuildBSPNodeOptions): NodeBSP | null {
  const stack: BuildBSPNodeStackEntry[] = [
    {
      parentNode: null,
      polygons,
      depth: 0,
      isFrontNode: true,
    },
  ];

  let rootNode: NodeBSP | null = null;

  let maxDepthReached = false;
  while (stack.length > 0) {
    const { parentNode, polygons, depth, isFrontNode } = ensureDefined(stack.pop());

    if (depth > maxDepth) {
      maxDepthReached = true;
      continue;
    }

    if (polygons.length === 0) {
      continue;
    }

    const plane = selectFirstPlaneBSP(polygons);
    if (!plane) {
      continue;
    }

    const frontPolygons: PolygonBSP[] = [];
    const backPolygons: PolygonBSP[] = [];
    const coplanarPolygons: PolygonBSP[] = [];
    const newLines: LineSplitBSP[] = [];

    /*
    We automatically classify the first polygon as coplanar. Then, at the loop, 
    we start from the second polygon. This is done to avoid infinite recursion 
    because of the first polygon being classified as front or back due to tolerance issues.
    This is an issue when some of the input polygons are really big, with
    high absolutes values in at least one of the coordinates.
    */
    const firstPolygon = polygons[0];
    coplanarPolygons.push(firstPolygon);

    for (let i = 1, l = polygons.length; i < l; i++) {
      const polygon = polygons[i];
      const { vertices } = polygon;
      const polygonSide = classifyPolygonBSP(plane, polygon, false, epsilon);

      switch (polygonSide) {
        case PlaneSide.INTERSECTS: {
          const { frontPolygon, backPolygon, splitLine } = splitPolygonPlaneBSP(plane, polygon, epsilon);

          if (polygon.extendedData && splitLine) {
            newLines.push(lineSplitBSPNew(frontPolygon, splitLine));
          }

          frontPolygons.push(frontPolygon);
          backPolygons.push(backPolygon);
          break;
        }
        case PlaneSide.BACK:
          backPolygons.push(polygon);
          break;
        case PlaneSide.FRONT:
          for (let j = 0, lj = vertices.length; j < lj; j += 3) {
            const nextJ = (j + 3) % lj;

            const edge = lineBSPNew(
              vertices[j],
              vertices[j + 1],
              vertices[j + 2],
              vertices[nextJ],
              vertices[nextJ + 1],
              vertices[nextJ + 2],
            );
            const edgeSide = classifyLineBSP(plane, edge, epsilon);

            if (edgeSide === PlaneSide.COPLANAR) {
              newLines.push(lineSplitBSPNew(polygon, edge));
            }
          }
          frontPolygons.push(polygon);
          break;
        default:
          if (planeBSPDot(plane, polygon.plane) < 0.0) {
            frontPolygons.push(polygon);
          } else {
            coplanarPolygons.push(polygon);
          }
      }
    }

    for (let i = 0, l = newLines.length; i < l; i++) {
      const { frontPolygon, line } = newLines[i];
      const { extendedData } = frontPolygon;

      if (!extendedData) {
        continue;
      }

      const { splitLines, id, hatchProperties, aabb } = extendedData;
      const materialId = hatchProperties?.hatchMaterial;

      for (let j = 0, lj = coplanarPolygons.length; j < lj; j++) {
        const coplanarPolygon = coplanarPolygons[j];
        const { extendedData: coplanarExtendedData } = coplanarPolygon;

        if (!coplanarExtendedData) {
          continue;
        }

        const {
          edgePlanes,
          id: coplanarId,
          hatchProperties: coplanarHatchProperties,
          aabb: coplanarAABB,
        } = coplanarExtendedData;

        if (
          edgePlanes.length === 0 ||
          id === coplanarId ||
          !aabb3Overlaps(aabb, coplanarAABB, POLYGON_AABB_TOLERANCE)
        ) {
          continue;
        }

        const coplanarMaterialId = coplanarHatchProperties?.hatchMaterial;
        const coplanarSide = classifyPolygonBSP(frontPolygon.plane, coplanarPolygon);

        const clipLine = clipLineOutsideConvexVol(
          line,
          edgePlanes,
          materialId === coplanarMaterialId || coplanarSide === PlaneSide.BACK,
          epsilon,
        );
        if (clipLine) {
          splitLines.push(clipLine);
        }
      }
    }

    const newNode: NodeBSP = nodeBSPNew(plane, coplanarPolygons, parentNode, null, null, userData);

    if (!parentNode) {
      rootNode = newNode;
    } else {
      if (isFrontNode) {
        parentNode.frontNode = newNode;
      } else {
        parentNode.backNode = newNode;
      }
    }

    if (frontPolygons.length > 0) {
      stack.push({
        parentNode: newNode,
        polygons: frontPolygons,
        depth: depth + 1,
        isFrontNode: true,
      });
    }

    if (backPolygons.length > 0) {
      stack.push({
        parentNode: newNode,
        polygons: backPolygons,
        depth: depth + 1,
        isFrontNode: false,
      });
    }
  }

  if (maxDepthReached) {
    maxDepthErrorHandle();
  }

  return rootNode;
}

interface RemoveCoplanarLinesStackEntry {
  readonly node: NodeBSP | null;
  readonly srcPolygons: PolygonBSP[];
}

export function removeCoplanarLinesBSP(
  node: NodeBSP | null,
  srcPolygons: PolygonBSP[],
  epsilon: number = BSP_EPSILON,
  lineEpsilon: number = REMOVE_COPLANAR_LINES_EPSILON,
) {
  if (!node) {
    return;
  }

  const stack: RemoveCoplanarLinesStackEntry[] = [{ node, srcPolygons }];

  while (stack.length > 0) {
    const { node: currNode, srcPolygons: currSrcPolygons } = ensureDefined(stack.pop());

    if (!currNode) {
      continue;
    }

    const { plane, polygons: nodePolygons, frontNode, backNode } = currNode;

    const frontPolygons: PolygonBSP[] = [];
    const backPolygons: PolygonBSP[] = [];
    const coplanarPolygons: PolygonBSP[] = [];

    for (let i = 0, l = currSrcPolygons.length; i < l; i++) {
      const polygon = currSrcPolygons[i];
      const polygonSide = classifyPolygonBSP(plane, polygon, true, epsilon);

      if (polygonSide === PlaneSide.INTERSECTS) {
        frontPolygons.push(polygon);
        backPolygons.push(polygon);
      } else if (polygonSide === PlaneSide.BACK) {
        backPolygons.push(polygon);
      } else if (polygonSide === PlaneSide.FRONT) {
        frontPolygons.push(polygon);
      } else {
        if (planeBSPDot(plane, polygon.plane) < 0.0) {
          frontPolygons.push(polygon);
        } else {
          coplanarPolygons.push(polygon);
        }
      }
    }

    for (let a = 0, la = nodePolygons.length; a < la; a++) {
      const polygonA = nodePolygons[a];
      const { vertices: aVertices, extendedData: extendedDataA } = polygonA;
      if (!extendedDataA) {
        continue;
      }
      const { id: ida, hatchProperties: aHatchProperties, neighborNormals, aabb: aabbA } = extendedDataA;
      let { visibleLines: aVisibleLines } = extendedDataA;

      const aMaterialKey = aHatchProperties?.materialKey;
      if (aVertices.length > 0 && neighborNormals.length === 0) {
        for (let b = 0, lb = coplanarPolygons.length; b < lb; b++) {
          const polygonB = coplanarPolygons[b];
          const { extendedData: extendedDataB } = polygonB;
          if (!extendedDataB) {
            continue;
          }
          const { id: idb, hatchProperties: bHatchProperties, edgePlanes: bEdgePlanes, aabb: aabbB } = extendedDataB;
          const bMaterialKey = bHatchProperties?.materialKey;
          if (ida !== idb && aMaterialKey === bMaterialKey) {
            const overlap = aabb3Overlaps(aabbA, aabbB, POLYGON_AABB_TOLERANCE)
              ? polygonOverlapping(polygonA, polygonB, true, lineEpsilon)
              : false;
            const clippedLines = clipLinesInsideConvexVol(aVisibleLines, bEdgePlanes, !overlap, lineEpsilon);
            aVisibleLines = clippedLines;
          }
        }
      }

      extendedDataA.visibleLines = aVisibleLines;
    }

    if (frontPolygons.length > 0) {
      stack.push({ node: frontNode, srcPolygons: frontPolygons });
    }
    if (backPolygons.length > 0) {
      stack.push({ node: backNode, srcPolygons: backPolygons });
    }
  }
}

export function addPolygonsOneByOneToBSP(
  node: NodeBSP | null,
  parentNode: NodeBSP | null,
  polygons: PolygonBSP[],
  epsilon = BSP_EPSILON,
  userData?: any,
): NodeBSP | null {
  for (let i = 0, l = polygons.length; i < l; i++) {
    const frontPolygons: PolygonBSP[] = [];
    splitPolygonBSP({
      node,
      polygon: polygons[i],
      coplanarsToFront: true,
      frontPolygons,
    });

    node = addPolygonsToBSP(node, parentNode, frontPolygons, epsilon, userData);
  }

  return node;
}

export function addPolygonsToBSP(
  node: NodeBSP | null,
  parentNode: NodeBSP | null,
  polygons: PolygonBSP[],
  epsilon = BSP_EPSILON,
  userData?: any,
): NodeBSP | null {
  if (!polygons) {
    return node;
  }

  polygons = polygons.filter((p) => !!p);
  const plane = selectFirstPlaneBSP(polygons);

  if (!plane) {
    return node;
  }

  const frontPolygons: PolygonBSP[] = [];
  const backPolygons: PolygonBSP[] = [];

  /*
  If a new node is created, we automatically classify the first polygon
  as coplanar. Then, at the loop, we start from the second polygon.
  This is done to avoid infinite recursion because of the first polygon
  being classified as front or back due to tolerance issues.
  This is an issue when some of the input polygons are really big, with
  high absolutes values in at least one of the coordinates.
  */
  let startI = 0;
  if (!node) {
    node = nodeBSPNew(plane, [], parentNode, null, null, userData);
    if (ADD_DEBUG_POLYGONS_TO_BSP) {
      node.polygons.push(polygons[0]);
    }
    startI = 1;
  }

  const { plane: nodePlane, frontNode, backNode } = node;

  for (let i = startI, l = polygons.length; i < l; i++) {
    const polygon = polygons[i];
    const polygonSide = classifyPolygonBSP(nodePlane, polygon, false, epsilon);

    switch (polygonSide) {
      case PlaneSide.BACK:
        backPolygons.push(polygon);
        break;
      case PlaneSide.FRONT:
        frontPolygons.push(polygon);
        break;
      case PlaneSide.INTERSECTS:
        const { frontPolygon, backPolygon } = splitPolygonPlaneBSP(nodePlane, polygon, epsilon);

        frontPolygons.push(frontPolygon);
        backPolygons.push(backPolygon);
        break;
      default:
        if (ADD_DEBUG_POLYGONS_TO_BSP) {
          node.polygons.push(polygon);
        }

        if (planeBSPDot(nodePlane, polygon.plane) < 0.0) {
          frontPolygons.push(polygon);
        }
    }
  }

  if (frontPolygons.length > 0) {
    node.frontNode = addPolygonsToBSP(frontNode, node, frontPolygons, epsilon, userData);
  }

  if (backPolygons.length > 0) {
    node.backNode = addPolygonsToBSP(backNode, node, backPolygons, epsilon, userData);
  }

  return node;
}

/*
addOutsidePolygonsToBSP is an optimization for doing 2 different BSP operations in one pass,
without traversing the tree twice. It's equivalet to calling splitPolygonsBSP for getting the
polygons outside the tree, and then calling addPolygonsToBSP for adding them to the tree.
*/

interface AddOutsidePolygonsToBSPOptions {
  readonly node: NodeBSP | null;
  readonly polygons: PolygonBSP[];
  readonly parentNode?: NodeBSP | null;
  readonly coplanarsToFront?: boolean;
  readonly checkCoplanarFacing?: boolean;
  readonly keepOutsideCoplanars?: boolean;
  readonly epsilon?: number;
  readonly userData?: any;
}

export function addOutsidePolygonsToBSP({
  node,
  polygons,
  parentNode = null,
  coplanarsToFront = false,
  checkCoplanarFacing = false,
  keepOutsideCoplanars = false,
  epsilon = BSP_EPSILON,
  userData,
}: AddOutsidePolygonsToBSPOptions): NodeBSP | null {
  if (!polygons) {
    return node;
  }

  const addOutsidePolygonsToBSPImpl = (
    node: NodeBSP | null,
    parentNode: NodeBSP | null,
    polygons: PolygonBSP[],
    isFrontNode: boolean,
  ): NodeBSP | null => {
    if (!node) {
      return isFrontNode ? addPolygonsToBSP(node, parentNode, polygons, epsilon, userData) : null;
    }

    const frontPolygons: PolygonBSP[] = [];
    const backPolygons: PolygonBSP[] = [];

    const { plane: nodePlane, frontNode, backNode } = node;

    for (let i = 0, l = polygons.length; i < l; i++) {
      const polygon = polygons[i];

      const polygonSide = classifyPolygonBSP(nodePlane, polygon, false, epsilon);

      switch (polygonSide) {
        case PlaneSide.BACK:
          backPolygons.push(polygon);
          break;
        case PlaneSide.FRONT:
          frontPolygons.push(polygon);
          break;
        case PlaneSide.INTERSECTS:
          const { frontPolygon, backPolygon } = splitPolygonPlaneBSP(nodePlane, polygon, epsilon);

          frontPolygons.push(frontPolygon);
          backPolygons.push(backPolygon);
          break;
        default:
          const hasOppNormal = planeBSPDot(nodePlane, polygon.plane) < 0;

          if (coplanarsToFront || (checkCoplanarFacing && hasOppNormal)) {
            if (hasOppNormal) {
              frontPolygons.push(polygon);
            } else if (keepOutsideCoplanars) {
              backPolygons.push(polygon);
            }
          } else {
            if (hasOppNormal) {
              backPolygons.push(polygon);
            } else if (keepOutsideCoplanars) {
              frontPolygons.push(polygon);
            }
          }
      }
    }

    if (backPolygons.length > 0) {
      node.backNode = addOutsidePolygonsToBSPImpl(backNode, node, backPolygons, false);
    }

    if (frontPolygons.length > 0) {
      node.frontNode = addOutsidePolygonsToBSPImpl(frontNode, node, frontPolygons, true);
    }

    return node;
  };

  return addOutsidePolygonsToBSPImpl(
    node,
    parentNode,
    polygons.filter((p) => !!p),
    true,
  );
}

export function isValidNodeBSP(node: NodeBSP | null): node is NodeBSP {
  if (!node) {
    return false;
  }
  return true;
}

export function addConvexPlanesToBSPLeaf(
  planes: PlaneBSP[],
  parentNode: NodeBSP | null = null,
  userData?: any,
): NodeBSP | null {
  let rootNode: NodeBSP | null = null;
  for (let i = 0, l = planes.length; i < l; i++) {
    const newNode = nodeBSPNew(planes[i], [], parentNode, null, null, userData);
    if (!rootNode) {
      rootNode = newNode;
    } else if (parentNode) {
      parentNode.backNode = newNode;
    }

    parentNode = newNode;
  }

  return rootNode;
}

interface AddOcclusionPolygonToBSPOptions {
  readonly node: NodeBSP | null;
  readonly isPerspectiveMatrix: boolean;
  readonly parentNode?: NodeBSP | null;
  readonly polygon: PolygonBSP;
  readonly zfar: number;
  readonly userData?: any;
  readonly epsilon?: number;
  readonly maxDepth?: number;
  readonly maxDepthErrorHandle?: MaxBSPDepthErrorHandler;
}

interface AddOcclusionPolygonToBSPStackEntry {
  readonly node: NodeBSP | null;
  readonly isFrontNode: boolean;
  readonly parentNode: NodeBSP | null;
  readonly polygon: PolygonBSP;
  readonly depth: number;
}

export function addOcclusionPolygonToBSP({
  node,
  isPerspectiveMatrix,
  parentNode = null,
  polygon,
  zfar,
  userData,
  epsilon = BSP_EPSILON,
  maxDepth = MAX_BSP_DEPTH,
  maxDepthErrorHandle = () => {
    console.warn("addOcclusionPolygonToBSP max depth reached");
  },
}: AddOcclusionPolygonToBSPOptions): NodeBSP | null {
  const { plane } = polygon;

  const offset = 2.0 * epsilon;
  const nX = 0;
  const nY = 0;
  const nZ = 1;

  let capPlane = planeBSPCopy(plane);
  if (isPerspectiveMatrix) {
    capPlane = planeBSPNew(-capPlane.nX, -capPlane.nY, -capPlane.nZ, -capPlane.dist);
  }
  capPlane.dist += offset;

  const stack: AddOcclusionPolygonToBSPStackEntry[] = [
    {
      node,
      isFrontNode: true,
      parentNode,
      polygon,
      depth: 0,
    },
  ];

  let result: NodeBSP | null = node;
  let maxDepthReached = false;

  while (stack.length > 0) {
    const { node, isFrontNode, parentNode, polygon, depth } = ensureDefined(stack.pop());

    if (depth > maxDepth) {
      maxDepthReached = true;
      continue;
    }

    if (!node) {
      if (isFrontNode) {
        const { vertices } = polygon;
        const edgePlanes: PlaneBSP[] = [];

        for (let i = 0, l = vertices.length; i < l; i += 3) {
          const nextI = (i + 3) % l;

          const aX = vertices[i];
          const aY = vertices[i + 1];
          const aZ = vertices[i + 2];

          const bX = vertices[nextI];
          const bY = vertices[nextI + 1];
          const bZ = vertices[nextI + 2];

          const side1 = parentNode ? vec3PlaneBSPSide(aX, aY, aZ, parentNode.plane, epsilon) : PlaneSide.FRONT;
          const side2 = parentNode ? vec3PlaneBSPSide(bX, bY, bZ, parentNode.plane, epsilon) : PlaneSide.FRONT;

          if (side1 !== PlaneSide.COPLANAR || side2 !== PlaneSide.COPLANAR) {
            const edgePlane = buildEdgePlane(aX, aY, aZ, bX, bY, bZ, nX * zfar, nY * zfar, nZ * zfar);

            if (edgePlane) {
              edgePlane.dist += offset;
              edgePlanes.push(edgePlane);
            }
          }
        }

        if (edgePlanes.length > 0) {
          edgePlanes.push(capPlane);
          const newNode = addConvexPlanesToBSPLeaf(edgePlanes, parentNode, userData);
          if (parentNode) {
            parentNode.frontNode = newNode;
          } else {
            result = newNode;
          }
        }
      }

      continue;
    }

    const side = classifyPolygonBSP(node.plane, polygon, false, epsilon);

    if (side === PlaneSide.INTERSECTS) {
      const { frontPolygon, backPolygon } = splitPolygonPlaneBSP(node.plane, polygon, epsilon);

      stack.push({
        node: node.frontNode,
        isFrontNode: true,
        parentNode: node,
        polygon: frontPolygon,
        depth: depth + 1,
      });

      stack.push({
        node: node.backNode,
        isFrontNode: false,
        parentNode: node,
        polygon: backPolygon,
        depth: depth + 1,
      });
    } else if (side === PlaneSide.BACK) {
      stack.push({
        node: node.backNode,
        isFrontNode: false,
        parentNode: node,
        polygon,
        depth: depth + 1,
      });
    } else if (side === PlaneSide.FRONT) {
      stack.push({
        node: node.frontNode,
        isFrontNode: true,
        parentNode: node,
        polygon,
        depth: depth + 1,
      });
    }
  }

  if (maxDepthReached) {
    maxDepthErrorHandle();
  }

  return result;
}

const intersectionLinePlaneBSPResult = {
  x: -0,
  y: -0,
  z: -0,
  t: -0,
};

export function intersectionLinePlaneBSP(
  plane: PlaneBSP,
  x1: number,
  y1: number,
  z1: number,
  x2: number,
  y2: number,
  z2: number,
  epsilon: number = BSP_EPSILON,
): IntersectionPointBSP | null {
  const { nX, nY, nZ, dist } = plane;

  const dirX = x2 - x1;
  const dirY = y2 - y1;
  const dirZ = z2 - z1;

  const den = nX * dirX + nY * dirY + nZ * dirZ;

  if (Math.abs(den) < epsilon) {
    return null;
  }

  const t = (dist - (nX * x1 + nY * y1 + nZ * z1)) / den;

  intersectionLinePlaneBSPResult.x = x1 + dirX * t;
  intersectionLinePlaneBSPResult.y = y1 + dirY * t;
  intersectionLinePlaneBSPResult.z = z1 + dirZ * t;
  intersectionLinePlaneBSPResult.t = t;

  return intersectionLinePlaneBSPResult;
}

function intersectionRayPlaneBSP(
  plane: PlaneBSP,
  x1: number,
  y1: number,
  z1: number,
  dirX: number,
  dirY: number,
  dirZ: number,
  epsilon: number = BSP_EPSILON,
): IntersectionPointBSP | null {
  const { nX, nY, nZ, dist } = plane;

  const den = nX * dirX + nY * dirY + nZ * dirZ;

  if (Math.abs(den) < epsilon) {
    return null;
  }

  const t = (dist - (nX * x1 + nY * y1 + nZ * z1)) / den;

  intersectionLinePlaneBSPResult.x = x1 + dirX * t;
  intersectionLinePlaneBSPResult.y = y1 + dirY * t;
  intersectionLinePlaneBSPResult.z = z1 + dirZ * t;
  intersectionLinePlaneBSPResult.t = t;

  return intersectionLinePlaneBSPResult;
}

export function pointInsideConvexVol(
  x: number,
  y: number,
  z: number,
  convexVol: PlaneBSP[],
  clipCoplanar: boolean = true,
): boolean {
  for (let i = 0, l = convexVol.length; i < l; i++) {
    const side = vec3PlaneBSPSide(x, y, z, convexVol[i]);

    if ((!clipCoplanar && side !== PlaneSide.BACK) || (clipCoplanar && side === PlaneSide.FRONT)) {
      return false;
    }
  }

  return true;
}

export function polygonOverlapping(
  polygonA: PolygonBSP,
  polygonB: PolygonBSP,
  clipCoplanar: boolean = true,
  epsilon: number = BSP_EPSILON,
): boolean {
  let separationPlaneFound = false;

  const { vertices: verticesA, plane: planeA, extendedData: extendedDataA } = polygonA;
  const { vertices: verticesB, plane: planeB, extendedData: extendedDataB } = polygonB;

  const edgePlanesA = extendedDataA?.edgePlanes ?? [];
  const edgePlanesB = extendedDataB?.edgePlanes ?? [];

  const { nX: anX, nY: anY, nZ: anZ } = planeA;
  const { nX: bnX, nY: bnY, nZ: bnZ } = planeB;

  if (edgePlanesA.length === 0) {
    for (let l = verticesA.length - 3, i = l - 3; i >= 0; i -= 3) {
      const nextI = i >= 3 ? i - 3 : l - 3;

      const edgePlane = buildEdgePlane(
        verticesA[i],
        verticesA[i + 1],
        verticesA[i + 2],
        verticesA[nextI],
        verticesA[nextI + 1],
        verticesA[nextI + 2],
        anX,
        anY,
        anZ,
      );

      if (edgePlane) {
        edgePlanesA.push(edgePlane);
      }
    }
  }

  if (edgePlanesB.length === 0) {
    for (let l = verticesB.length - 3, i = l - 3; i >= 0; i -= 3) {
      const nextI = i >= 3 ? i - 3 : l - 3;

      const edgePlane = buildEdgePlane(
        verticesB[i],
        verticesB[i + 1],
        verticesB[i + 2],
        verticesB[nextI],
        verticesB[nextI + 1],
        verticesB[nextI + 2],
        bnX,
        bnY,
        bnZ,
      );

      if (edgePlane) {
        edgePlanesB.push(edgePlane);
      }
    }
  }

  for (let a = 0, la = edgePlanesA.length; a < la; a++) {
    const edgeA = edgePlanesA[a];

    let overlap = false;
    for (let i = 0, l = verticesB.length; i < l; i += 3) {
      const side = vec3PlaneBSPSide(verticesB[i], verticesB[i + 1], verticesB[i + 2], edgeA, epsilon);

      if ((!clipCoplanar && side !== PlaneSide.FRONT) || (clipCoplanar && side === PlaneSide.BACK)) {
        overlap = true;
        break;
      }
    }

    if (!overlap) {
      separationPlaneFound = true;
      break;
    }
  }

  if (!separationPlaneFound) {
    for (let b = 0, lb = edgePlanesB.length; b < lb; b++) {
      const edgeB = edgePlanesB[b];

      let overlap = false;
      for (let i = 0, l = verticesA.length; i < l; i += 3) {
        const side = vec3PlaneBSPSide(verticesA[i], verticesA[i + 1], verticesA[i + 2], edgeB, epsilon);

        if ((!clipCoplanar && side !== PlaneSide.FRONT) || (clipCoplanar && side === PlaneSide.BACK)) {
          overlap = true;
          break;
        }
      }

      if (!overlap) {
        separationPlaneFound = true;
        break;
      }
    }
  }

  return !separationPlaneFound;
}

export function clipLinesOutsideConvexVol(
  lines: LineBSP[],
  convexVol: PlaneBSP[],
  clipCoplanar: boolean = true,
  epsilon: number = BSP_EPSILON,
): LineBSP[] {
  const clippedLines: LineBSP[] = [];

  for (let i = 0, l = lines.length; i < l; i++) {
    const clippedLine = clipLineOutsideConvexVol(lines[i], convexVol, clipCoplanar, epsilon);

    if (clippedLine) {
      clippedLines.push(clippedLine);
    }
  }

  return clippedLines;
}

export function clipLineOutsideConvexVol(
  line: LineBSP,
  convexVol: PlaneBSP[],
  clipCoplanar: boolean = true,
  epsilon: number = BSP_EPSILON,
): LineBSP | null {
  let { x1: xc1, y1: yc1, z1: zc1, x2: xc2, y2: yc2, z2: zc2 } = line;

  const dirX = xc2 - xc1;
  const dirY = yc2 - yc1;
  const dirZ = zc2 - zc1;

  for (let i = 0, l = convexVol.length; i < l; i++) {
    const plane = convexVol[i];
    const side = classifySegmentBSP(plane, xc2, yc2, zc2, xc1, yc1, zc1, epsilon);

    if (side === PlaneSide.INTERSECTS) {
      const {
        x: xI,
        y: yI,
        z: zI,
      } = intersectionRayPlaneBSP(plane, xc1, yc1, zc1, dirX, dirY, dirZ, epsilon) ?? emptyLinePlaneBSPResult;

      if (vec3PlaneBSPSide(xc1, yc1, zc1, plane, epsilon) === PlaneSide.FRONT) {
        xc1 = xI;
        yc1 = yI;
        zc1 = zI;
      } else {
        xc2 = xI;
        yc2 = yI;
        zc2 = zI;
      }
    } else if ((clipCoplanar && side !== PlaneSide.BACK) || (!clipCoplanar && side === PlaneSide.FRONT)) {
      return null;
    }
  }

  const newDirX = xc2 - xc1;
  const newDirY = yc2 - yc1;
  const newDirZ = zc2 - zc1;

  return vec3Dot(dirX, dirY, dirZ, newDirX, newDirY, newDirZ) > BSP_EPSILON
    ? lineBSPNew(xc1, yc1, zc1, xc2, yc2, zc2)
    : null;
}

export function clipPolygonOutsideConvexVol(
  polygon: PolygonBSP,
  convexVol: PlaneBSP[],
  clipCoplanar: boolean = true,
): PolygonBSP | null {
  let clippedPolygon = polygonBSPCopy(polygon);

  for (let i = 0, l = convexVol.length; i < l; i++) {
    const plane = convexVol[i];
    const side = classifyPolygonBSP(plane, clippedPolygon);

    if (side === PlaneSide.INTERSECTS) {
      clippedPolygon = splitPolygonPlaneBSP(plane, clippedPolygon).backPolygon;
    } else if ((clipCoplanar && side !== PlaneSide.BACK) || (!clipCoplanar && side === PlaneSide.FRONT)) {
      return null;
    }
  }

  return clippedPolygon;
}

export function clipLinesInsideConvexVol(
  lines: LineBSP[],
  convexVol: PlaneBSP[],
  clipCoplanar: boolean = true,
  epsilon: number = BSP_EPSILON,
  outputLines: LineBSP[] = [],
): LineBSP[] {
  for (let i = 0, l = lines.length; i < l; i++) {
    const line = lines[i];
    const insideLine = clipLineOutsideConvexVol(line, convexVol, !clipCoplanar, epsilon);

    if (!insideLine) {
      outputLines.push(lineBSPCopy(line));
      continue;
    }

    const { x1, y1, z1, x2, y2, z2 } = line;

    const xa1 = x1;
    const ya1 = y1;
    const za1 = z1;

    const { x1: xa2, y1: ya2, z1: za2, x2: xb1, y2: yb1, z2: zb1 } = insideLine;

    const xb2 = x2;
    const yb2 = y2;
    const zb2 = z2;

    const d1 = vec3DistSquared(xa1, ya1, za1, xa2, ya2, za2) > BSP_EPSILON_SQUARED;
    const d2 = vec3DistSquared(xb1, yb1, zb1, xb2, yb2, zb2) > BSP_EPSILON_SQUARED;

    if (d1 && d2) {
      outputLines.push(lineBSPNew(xa1, ya1, za1, xa2, ya2, za2), lineBSPNew(xb1, yb1, zb1, xb2, yb2, zb2));
    } else if (d1) {
      outputLines.push(lineBSPNew(xa1, ya1, za1, xa2, ya2, za2));
    } else if (d2) {
      outputLines.push(lineBSPNew(xb1, yb1, zb1, xb2, yb2, zb2));
    }
  }

  return outputLines;
}

interface SplitLineBSPOptions {
  readonly node: NodeBSP | null;
  readonly coplanarsToFront: boolean;
  readonly line: LineBSP;
  readonly frontSegments?: LineBSP[];
  readonly backSegments?: LineBSP[];
  readonly epsilon?: number;
  readonly maxDepth?: number;
  readonly maxDepthErrorHandle?: MaxBSPDepthErrorHandler;
}

interface SplitLineBSPStackEntry {
  readonly node: NodeBSP | null;
  readonly isFrontNode: boolean;
  readonly line: LineBSP;
  readonly depth: number;
}

export function splitLineBSP({
  node,
  coplanarsToFront,
  line,
  frontSegments,
  backSegments,
  epsilon = BSP_EPSILON,
  maxDepth = MAX_BSP_DEPTH,
  maxDepthErrorHandle = () => {
    console.warn("splitLineBSP max depth reached");
  },
}: SplitLineBSPOptions) {
  const stack: SplitLineBSPStackEntry[] = [
    {
      node,
      isFrontNode: true,
      line,
      depth: 0,
    },
  ];

  let maxDepthReached = false;

  while (stack.length > 0) {
    const { node, isFrontNode, line, depth } = ensureDefined(stack.pop());

    if (depth > maxDepth) {
      maxDepthReached = true;
      continue;
    }

    if (!node) {
      if (isFrontNode && frontSegments) {
        frontSegments.push(line);
      }
      if (!isFrontNode && backSegments) {
        backSegments.push(line);
      }

      continue;
    }

    const { plane, frontNode, backNode } = node;

    const side = classifyLineBSP(plane, line, epsilon);

    if (side === PlaneSide.INTERSECTS) {
      const { x1, y1, z1, x2, y2, z2 } = line;

      const side1 = vec3PlaneBSPSide(x1, y1, z1, plane, epsilon);
      const {
        x: xI,
        y: yI,
        z: zI,
      } = intersectionLinePlaneBSP(plane, x1, y1, z1, x2, y2, z2, epsilon) ?? emptyLinePlaneBSPResult;

      if (side1 === PlaneSide.BACK) {
        stack.push({
          node: frontNode,
          isFrontNode: true,
          line: lineBSPNew(xI, yI, zI, x2, y2, z2),
          depth: depth + 1,
        });
        stack.push({
          node: backNode,
          isFrontNode: false,
          line: lineBSPNew(x1, y1, z1, xI, yI, zI),
          depth: depth + 1,
        });
      } else {
        stack.push({
          node: backNode,
          isFrontNode: false,
          line: lineBSPNew(xI, yI, zI, x2, y2, z2),
          depth: depth + 1,
        });
        stack.push({
          node: frontNode,
          isFrontNode: true,
          line: lineBSPNew(x1, y1, z1, xI, yI, zI),
          depth: depth + 1,
        });
      }
    } else if (side === PlaneSide.BACK) {
      stack.push({
        node: backNode,
        isFrontNode: false,
        line,
        depth: depth + 1,
      });
    } else if (side === PlaneSide.FRONT) {
      stack.push({
        node: frontNode,
        isFrontNode: true,
        line,
        depth: depth + 1,
      });
    } else if (side === PlaneSide.COPLANAR) {
      if (coplanarsToFront) {
        stack.push({
          node: frontNode,
          isFrontNode: true,
          line,
          depth: depth + 1,
        });
      } else {
        stack.push({
          node: backNode,
          isFrontNode: false,
          line,
          depth: depth + 1,
        });
      }
    }
  }

  if (maxDepthReached) {
    maxDepthErrorHandle();
  }
}

interface SplitLinesBSPOptions {
  readonly node: NodeBSP | null;
  readonly coplanarsToFront: boolean;
  readonly segments: LineBSP[];
  readonly frontSegments?: LineBSP[];
  readonly backSegments?: LineBSP[];
  readonly epsilon?: number;
  readonly maxDepth?: number;
  readonly maxDepthErrorHandle?: MaxBSPDepthErrorHandler;
}

interface SplitLinesBSPStackEntry {
  readonly node: NodeBSP | null;
  readonly isFrontNode: boolean;
  readonly segments: LineBSP[];
  readonly depth: number;
}

export function splitLinesBSP({
  node,
  coplanarsToFront,
  segments,
  frontSegments,
  backSegments,
  epsilon = BSP_EPSILON,
  maxDepth = MAX_BSP_DEPTH,
  maxDepthErrorHandle = () => {
    console.warn("splitLinesBSP max depth reached");
  },
}: SplitLinesBSPOptions) {
  const stack: SplitLinesBSPStackEntry[] = [
    {
      node,
      isFrontNode: true,
      segments,
      depth: 0,
    },
  ];

  let maxDepthReached = false;

  while (stack.length > 0) {
    const { node, isFrontNode, segments, depth } = ensureDefined(stack.pop());

    if (depth > maxDepth) {
      maxDepthReached = true;
      continue;
    }

    if (!node) {
      if (isFrontNode && frontSegments) {
        frontSegments.push(...segments);
      }
      if (!isFrontNode && backSegments) {
        backSegments.push(...segments);
      }

      continue;
    }

    const { plane, frontNode, backNode } = node;

    const currFrontSegments: LineBSP[] = [];
    const currBackSegments: LineBSP[] = [];

    for (let i = 0, l = segments.length; i < l; i++) {
      const segment = segments[i];

      const side = classifyLineBSP(plane, segment, epsilon);

      if (side === PlaneSide.INTERSECTS) {
        const { x1, y1, z1, x2, y2, z2 } = segment;

        const side1 = vec3PlaneBSPSide(x1, y1, z1, plane, epsilon);
        const {
          x: xI,
          y: yI,
          z: zI,
        } = intersectionLinePlaneBSP(plane, x1, y1, z1, x2, y2, z2, epsilon) ?? emptyLinePlaneBSPResult;

        if (side1 === PlaneSide.BACK) {
          currBackSegments.push(lineBSPNew(x1, y1, z1, xI, yI, zI));
          currFrontSegments.push(lineBSPNew(xI, yI, zI, x2, y2, z2));
        } else {
          currFrontSegments.push(lineBSPNew(x1, y1, z1, xI, yI, zI));
          currBackSegments.push(lineBSPNew(xI, yI, zI, x2, y2, z2));
        }
      } else if (side === PlaneSide.BACK) {
        currBackSegments.push(segment);
      } else if (side === PlaneSide.FRONT) {
        currFrontSegments.push(segment);
      } else if (side === PlaneSide.COPLANAR) {
        if (coplanarsToFront) {
          currFrontSegments.push(segment);
        } else {
          currBackSegments.push(segment);
        }
      }
    }

    if (currFrontSegments.length > 0) {
      stack.push({
        node: frontNode,
        isFrontNode: true,
        segments: currFrontSegments,
        depth: depth + 1,
      });
    }

    if (currBackSegments.length > 0) {
      stack.push({
        node: backNode,
        isFrontNode: false,
        segments: currBackSegments,
        depth: depth + 1,
      });
    }
  }

  if (maxDepthReached) {
    maxDepthErrorHandle();
  }
}

interface SplitPolygonBSPOptions {
  readonly node: NodeBSP | null;
  readonly polygon: PolygonBSP;
  readonly coplanarsToFront: boolean;
  readonly frontPolygons?: PolygonBSP[];
  readonly backPolygons?: PolygonBSP[];
  readonly epsilon?: number;
  readonly checkCoplanarFacing?: boolean;
  readonly maxDepth?: number;
  readonly maxDepthErrorHandle?: MaxBSPDepthErrorHandler;
}

export function splitPolygonBSP({
  node,
  polygon,
  coplanarsToFront,
  frontPolygons,
  backPolygons,
  epsilon,
  checkCoplanarFacing,
  maxDepth,
  maxDepthErrorHandle,
}: SplitPolygonBSPOptions) {
  splitPolygonsBSP({
    node,
    polygons: [polygon],
    coplanarsToFront,
    frontPolygons,
    backPolygons,
    epsilon,
    checkCoplanarFacing,
    maxDepth,
    maxDepthErrorHandle,
  });
}

interface SplitPolygonsBSPOptions {
  readonly node: NodeBSP | null;
  readonly polygons: PolygonBSP[];
  readonly coplanarsToFront: boolean;
  readonly frontPolygons?: PolygonBSP[];
  readonly backPolygons?: PolygonBSP[];
  readonly epsilon?: number;
  readonly checkCoplanarFacing?: boolean;
  readonly maxDepth?: number;
  readonly maxDepthErrorHandle?: MaxBSPDepthErrorHandler;
}

interface SplitPolygonsBSPStackEntry {
  readonly node: NodeBSP | null;
  readonly polygons: PolygonBSP[];
  readonly isFrontNode: boolean;
  readonly depth: number;
}

export function splitPolygonsBSP({
  node,
  polygons,
  coplanarsToFront,
  frontPolygons,
  backPolygons,
  epsilon = BSP_EPSILON,
  checkCoplanarFacing = false,
  maxDepth = MAX_BSP_DEPTH,
  maxDepthErrorHandle = () => {
    console.warn("splitPolygonsBSP max depth reached");
  },
}: SplitPolygonsBSPOptions) {
  const stack: SplitPolygonsBSPStackEntry[] = [
    {
      node,
      polygons,
      depth: 0,
      isFrontNode: true,
    },
  ];

  let maxDepthReached = false;

  while (stack.length > 0) {
    const { node, polygons, isFrontNode, depth } = ensureDefined(stack.pop());

    if (depth > maxDepth) {
      maxDepthReached = true;
      continue;
    }

    if (!node) {
      if (isFrontNode && frontPolygons) {
        frontPolygons.push(...polygons);
      }
      if (!isFrontNode && backPolygons) {
        backPolygons.push(...polygons);
      }

      continue;
    }

    const { plane, frontNode, backNode } = node;

    const currFrontPolygons: PolygonBSP[] = [];
    const currBackPolygons: PolygonBSP[] = [];
    for (let i = 0, l = polygons.length; i < l; i++) {
      const polygon = polygons[i];
      const side = classifyPolygonBSP(plane, polygon, false, epsilon);

      if (side === PlaneSide.INTERSECTS) {
        const { frontPolygon, backPolygon } = splitPolygonPlaneBSP(plane, polygon, epsilon);

        currBackPolygons.push(backPolygon);
        currFrontPolygons.push(frontPolygon);
      } else if (side === PlaneSide.BACK) {
        currBackPolygons.push(polygon);
      } else if (side === PlaneSide.FRONT) {
        currFrontPolygons.push(polygon);
      } else {
        if (coplanarsToFront || (checkCoplanarFacing && planeBSPDot(plane, polygon.plane) < 0)) {
          currFrontPolygons.push(polygon);
        } else {
          currBackPolygons.push(polygon);
        }
      }
    }

    if (currFrontPolygons.length > 0) {
      stack.push({
        node: frontNode,
        polygons: currFrontPolygons,
        isFrontNode: true,
        depth: depth + 1,
      });
    }

    if (currBackPolygons.length > 0) {
      stack.push({
        node: backNode,
        polygons: currBackPolygons,
        isFrontNode: false,
        depth: depth + 1,
      });
    }
  }

  if (maxDepthReached) {
    maxDepthErrorHandle();
  }
}

export function findBSPLeaf(node: NodeBSP | null, x: number, y: number, z: number): NodeBSP | null {
  if (!node) {
    return null;
  }

  const pointSide = vec3PlaneBSPSide(x, y, z, node.plane);

  if (pointSide === PlaneSide.BACK) {
    return node.backNode ? findBSPLeaf(node.backNode, x, y, z) : node;
  } else {
    return node.frontNode ? findBSPLeaf(node.frontNode, x, y, z) : node;
  }
}

export function isPointInsideBSP(
  node: NodeBSP | null,
  x: number,
  y: number,
  z: number,
  coplanarsToFront: boolean = true,
  epsilon: number = BSP_EPSILON,
): boolean {
  if (!node) {
    return false;
  }

  const side = vec3PlaneBSPSide(x, y, z, node.plane, epsilon);

  if (side === PlaneSide.BACK || (side === PlaneSide.COPLANAR && !coplanarsToFront)) {
    if (node.backNode) {
      return isPointInsideBSP(node.backNode, x, y, z, coplanarsToFront, epsilon);
    } else {
      return true;
    }
  } else {
    if (node.frontNode) {
      return isPointInsideBSP(node.frontNode, x, y, z, coplanarsToFront, epsilon);
    } else {
      return false;
    }
  }
}

interface RayIntersectionBSPOptions {
  node: NodeBSP | null;
  isFrontNode?: boolean;
  coplanarsToFront?: boolean;
  x1: number;
  y1: number;
  z1: number;
  x2: number;
  y2: number;
  z2: number;
  sourceRayLen?: number;
  t1?: number;
  t2?: number;
  lastUserData?: number;
  entryPlane?: PlaneBSP;
  exitPlane?: PlaneBSP;
  epsilon?: number;
}

export function rayIntersectionBSP({
  node,
  isFrontNode = true,
  coplanarsToFront = false,
  x1,
  y1,
  z1,
  x2,
  y2,
  z2,
  sourceRayLen = vec3Dist(x1, y1, z1, x2, y2, z2),
  t1 = 0,
  t2 = 1,
  lastUserData = 0,
  entryPlane,
  exitPlane,
  epsilon = BSP_EPSILON,
}: RayIntersectionBSPOptions): RayIntersectionBSP | null {
  if (!node) {
    if (isFrontNode || t2 - t1 < DIST_EPSILON) {
      return null;
    } else {
      const [entryX, entryY, entryZ] = line3Eval(x1, y1, z1, x2, y2, z2, t1, true);
      const [exitX, exitY, exitZ] = line3Eval(x1, y1, z1, x2, y2, z2, t2, true);

      return {
        entryPoint:
          t1 > DIST_EPSILON
            ? {
                x: entryX,
                y: entryY,
                z: entryZ,
                t: t1,
                distToStart: sourceRayLen * t1,
                userData: lastUserData,
                nX: entryPlane?.nX ?? 0,
                nY: entryPlane?.nY ?? 0,
                nZ: entryPlane?.nZ ?? 0,
              }
            : undefined,
        exitPoint:
          t2 < 1.0 - DIST_EPSILON
            ? {
                x: exitX,
                y: exitY,
                z: exitZ,
                t: t2,
                distToStart: sourceRayLen * t2,
                userData: lastUserData,
                nX: exitPlane?.nX ?? 0,
                nY: exitPlane?.nY ?? 0,
                nZ: exitPlane?.nZ ?? 0,
              }
            : undefined,
      };
    }
  }

  const [ax, ay, az] = line3Eval(x1, y1, z1, x2, y2, z2, t1, true);
  const [bx, by, bz] = line3Eval(x1, y1, z1, x2, y2, z2, t2, true);

  const { plane, frontNode, backNode, userData } = node;
  const side = classifySegmentBSP(plane, ax, ay, az, bx, by, bz, epsilon);

  if (side === PlaneSide.INTERSECTS) {
    const side1 = vec3PlaneBSPSide(ax, ay, az, plane, epsilon);
    const { t } = intersectionLinePlaneBSP(plane, x1, y1, z1, x2, y2, z2) ?? intersectionLinePlaneBSPResult;

    let backRayIntersection: RayIntersectionBSP | null = null;
    let frontRayIntersection: RayIntersectionBSP | null = null;

    if (side1 === PlaneSide.FRONT) {
      frontRayIntersection = rayIntersectionBSP({
        node: frontNode,
        isFrontNode: true,
        coplanarsToFront,
        x1,
        y1,
        z1,
        x2,
        y2,
        z2,
        sourceRayLen,
        t1,
        t2: t,
        epsilon,
        lastUserData: userData,
        entryPlane,
        exitPlane: plane,
      });
      backRayIntersection = rayIntersectionBSP({
        node: backNode,
        isFrontNode: false,
        coplanarsToFront,
        x1,
        y1,
        z1,
        x2,
        y2,
        z2,
        sourceRayLen,
        t1: t,
        t2,
        epsilon,
        lastUserData: userData,
        entryPlane: plane,
        exitPlane,
      });
    } else {
      frontRayIntersection = rayIntersectionBSP({
        node: frontNode,
        isFrontNode: true,
        coplanarsToFront,
        x1,
        y1,
        z1,
        x2,
        y2,
        z2,
        sourceRayLen,
        t1: t,
        t2,
        epsilon,
        lastUserData: userData,
        entryPlane: plane,
        exitPlane,
      });
      backRayIntersection = rayIntersectionBSP({
        node: backNode,
        isFrontNode: false,
        coplanarsToFront,
        x1,
        y1,
        z1,
        x2,
        y2,
        z2,
        sourceRayLen,
        t1,
        t2: t,
        epsilon,
        lastUserData: userData,
        entryPlane,
        exitPlane: plane,
      });
    }

    let entryPoint: RayIntersectionPointBSP | undefined = undefined;
    if (backRayIntersection?.entryPoint && frontRayIntersection?.entryPoint) {
      entryPoint =
        backRayIntersection.entryPoint.t < frontRayIntersection.entryPoint.t
          ? backRayIntersection.entryPoint
          : frontRayIntersection.entryPoint;
    } else {
      entryPoint = backRayIntersection?.entryPoint ? backRayIntersection.entryPoint : frontRayIntersection?.entryPoint;
    }

    let exitPoint: RayIntersectionPointBSP | undefined = undefined;
    if (backRayIntersection?.exitPoint && frontRayIntersection?.exitPoint) {
      exitPoint =
        backRayIntersection.exitPoint.t > frontRayIntersection.exitPoint.t
          ? backRayIntersection.exitPoint
          : frontRayIntersection.exitPoint;
    } else {
      exitPoint = backRayIntersection?.exitPoint ? backRayIntersection.exitPoint : frontRayIntersection?.exitPoint;
    }

    return { entryPoint, exitPoint };
  } else if (side === PlaneSide.BACK) {
    return rayIntersectionBSP({
      node: backNode,
      isFrontNode: false,
      coplanarsToFront,
      x1,
      y1,
      z1,
      x2,
      y2,
      z2,
      sourceRayLen,
      t1,
      t2,
      epsilon,
      lastUserData,
      entryPlane,
      exitPlane,
    });
  } else if (side === PlaneSide.FRONT) {
    return rayIntersectionBSP({
      node: frontNode,
      isFrontNode: true,
      coplanarsToFront,
      x1,
      y1,
      z1,
      x2,
      y2,
      z2,
      sourceRayLen,
      t1,
      t2,
      epsilon,
      lastUserData,
      entryPlane,
      exitPlane,
    });
  } else {
    if (coplanarsToFront) {
      return rayIntersectionBSP({
        node: frontNode,
        isFrontNode: true,
        coplanarsToFront,
        x1,
        y1,
        z1,
        x2,
        y2,
        z2,
        sourceRayLen,
        t1,
        t2,
        epsilon,
        lastUserData,
        entryPlane,
        exitPlane,
      });
    } else {
      return rayIntersectionBSP({
        node: backNode,
        isFrontNode: false,
        coplanarsToFront,
        x1,
        y1,
        z1,
        x2,
        y2,
        z2,
        sourceRayLen,
        t1,
        t2,
        epsilon,
        lastUserData,
        entryPlane,
        exitPlane,
      });
    }
  }
}

interface RenderNodeBSPStackEntry {
  readonly node: NodeBSP | null;
  readonly renderSide?: number;
  readonly depth: number;
}

interface RenderNodeBSPOptions {
  readonly node: NodeBSP | null;
  readonly renderInfo: RenderInfoBSP;
  readonly order: TraversalOrderBSP;
  readonly culling: TraversalCullingBSP;
  readonly renderFunc: (node: NodeBSP, side: number) => void;
  readonly maxDepth?: number;
  readonly maxDepthErrorHandle?: MaxBSPDepthErrorHandler;
}

export function renderNodeBSP({
  node,
  renderInfo,
  order,
  culling,
  renderFunc,
  maxDepth = MAX_BSP_DEPTH,
  maxDepthErrorHandle = () => {
    console.warn("renderNodeBSP max depth reached");
  },
}: RenderNodeBSPOptions) {
  const stack: RenderNodeBSPStackEntry[] = [
    {
      node,
      depth: 0,
    },
  ];

  let maxDepthReached = false;

  while (stack.length > 0) {
    const { node, renderSide, depth } = ensureDefined(stack.pop());

    if (depth > maxDepth) {
      maxDepthReached = true;
      continue;
    }

    if (!node) {
      continue;
    }

    // render node directly and skip the rest of the logic
    if (renderSide !== undefined) {
      renderFunc(node, renderSide);
      continue;
    }

    // render the branch
    let visible = true;
    let side = 1.0;

    if (renderInfo.isPerspectiveMatrix) {
      const viewSide = vec3PlaneBSPSide(renderInfo.viewPosX, renderInfo.viewPosY, renderInfo.viewPosZ, node.plane);

      side = (viewSide === PlaneSide.BACK ? 1.0 : -1.0) * side;
    } else {
      side = vec3Dot(renderInfo.viewX, renderInfo.viewY, renderInfo.viewZ, node.plane.nX, node.plane.nY, node.plane.nZ);
    }

    if (culling === TraversalCullingBSP.FRONT) {
      visible = side >= DIST_EPSILON;
    } else if (culling === TraversalCullingBSP.BACK) {
      visible = side <= -DIST_EPSILON;
    }

    const finalSide = side * (order === TraversalOrderBSP.BACK_TO_FRONT ? 1.0 : -1.0);

    if (finalSide > 0) {
      // render the front node branch, the current node directly, and the back node branch
      // (they are inserted in the reverse order into the stack to ensure the correct order of rendering)
      stack.push({
        node: node.backNode,
        depth: depth + 1,
      });

      if (visible) {
        stack.push({
          node,
          renderSide: side,
          depth,
        });
      }

      stack.push({
        node: node.frontNode,
        depth: depth + 1,
      });
    } else {
      // render the front node branch, the current node directly, and the back node branch
      // (they are inserted in the reverse order into the stack to ensure the correct order of rendering)
      stack.push({
        node: node.frontNode,
        depth: depth + 1,
      });

      if (visible) {
        stack.push({
          node,
          renderSide: side,
          depth,
        });
      }

      stack.push({
        node: node.backNode,
        depth: depth + 1,
      });
    }
  }

  if (maxDepthReached) {
    maxDepthErrorHandle();
  }
}

/**
 * triangleSignedArea is a helper function for isDegeneratePolygonBSP. It looks up
 * a 3 vertex sequence from a PolygonBSP's vertices and computes its signed area.
 */
function triangleSignedArea(
  normalX: number,
  normalY: number,
  normalZ: number,
  vertices: number[],
  vertexOffset: number,
) {
  const len = vertices.length;

  const ax = vertices[(vertexOffset - 3 + len) % len];
  const ay = vertices[(vertexOffset - 2 + len) % len];
  const az = vertices[(vertexOffset - 1 + len) % len];

  const bx = vertices[vertexOffset];
  const by = vertices[vertexOffset + 1];
  const bz = vertices[vertexOffset + 2];

  const cx = vertices[(vertexOffset + 3) % len];
  const cy = vertices[(vertexOffset + 4) % len];
  const cz = vertices[(vertexOffset + 5) % len];

  const [dx1, dy1, dz1] = vec3Sub(ax, ay, az, bx, by, bz);
  const [dx2, dy2, dz2] = vec3Sub(cx, cy, cz, bx, by, bz);

  const [x, y, z] = vec3Cross(dx1, dy1, dz1, dx2, dy2, dz2);

  return vec3Dot(normalX, normalY, normalZ, x, y, z);
}

export function isDegeneratePolygonBSP(
  { vertices, plane, extendedData }: PolygonBSP,
  areaTolerance: number = AREA_TOLERANCE,
): boolean {
  if (vertices.length < 9) {
    return true;
  }

  const edgePlanes = extendedData?.edgePlanes;
  const { nX, nY, nZ } = plane;

  // fast path: tri case
  if (!edgePlanes && vertices.length === 9) {
    /**
     * A triangle with area less than area tolerance is degenerate.
     */
    return Math.abs(triangleSignedArea(nX, nY, nZ, vertices, 0)) < areaTolerance;
  }

  // fast path: quad case
  if (!edgePlanes && vertices.length === 12) {
    /**
     * Checking if any of the 4 triangles formed by consecutive sequences of the
     * quad's vertices are degenerate. If so, the quad is degenerate.
     */
    return (
      Math.abs(triangleSignedArea(nX, nY, nZ, vertices, 0)) < areaTolerance ||
      Math.abs(triangleSignedArea(nX, nY, nZ, vertices, 3)) < areaTolerance ||
      Math.abs(triangleSignedArea(nX, nY, nZ, vertices, 6)) < areaTolerance ||
      Math.abs(triangleSignedArea(nX, nY, nZ, vertices, 9)) < areaTolerance
    );
  }

  /**
   * General case: polygon with more than 4 vertices
   * triangleSignedArea is inlined here for performance reasons, since
   * we need to check collinear edges and coincident vertices.
   */
  const vertexCount = vertices.length;

  let ax = vertices[0];
  let ay = vertices[1];
  let az = vertices[2];

  let [dx1, dy1, dz1] = vec3Sub(
    vertices[vertexCount - 3],
    vertices[vertexCount - 2],
    vertices[vertexCount - 1],
    ax,
    ay,
    az,
  );

  let totalArea = 0;
  for (let i = 0; i < vertexCount; i += 3) {
    const bx = vertices[(i + 3) % vertexCount];
    const by = vertices[(i + 4) % vertexCount];
    const bz = vertices[(i + 5) % vertexCount];

    const [dx2, dy2, dz2] = vec3Sub(ax, ay, az, bx, by, bz);

    // check that we don't have coincident vertices
    if (vec3LenSquared(dx1, dy1, dz1) > DIST_EPSILON_SQUARED && vec3LenSquared(dx2, dy2, dz2) > DIST_EPSILON_SQUARED) {
      const [x, y, z] = vec3Cross(dx1, dy1, dz1, -dx2, -dy2, -dz2);
      const area = Math.abs(vec3Dot(nX, nY, nZ, x, y, z));

      // increase the total area
      totalArea += area;

      // look for zero area triangle
      if (area < areaTolerance && vec3Dot(dx1, dy1, dz1, dx2, dy2, dz2) < DIST_EPSILON) {
        return true;
      }
    }

    ax = bx;
    ay = by;
    az = bz;

    dx1 = dx2;
    dy1 = dy2;
    dz1 = dz2;
  }

  return totalArea < DIST_EPSILON_SQUARED;
}

interface BSPDepthStackEntry {
  readonly node: NodeBSP | null;
  readonly depth: number;
}

/**
 * This funciton is currently not used, but it's helpful for debugging.
 */
export function getBSPDepth(node: NodeBSP | null): number {
  const nodeStack: BSPDepthStackEntry[] = [
    {
      node,
      depth: 0,
    },
  ];

  let maxDepth = 0;

  while (nodeStack.length > 0) {
    const { node, depth } = ensureDefined(nodeStack.pop());

    if (!node) {
      continue;
    }

    maxDepth = Math.max(maxDepth, depth);

    nodeStack.push({
      node: node.backNode,
      depth: depth + 1,
    });

    nodeStack.push({
      node: node.frontNode,
      depth: depth + 1,
    });
  }

  return maxDepth;
}

export function getBSPPolygonCount(node: NodeBSP | null): number {
  if (!node) {
    return 0;
  }

  return node.polygons.length + getBSPPolygonCount(node.frontNode) + getBSPPolygonCount(node.backNode);
}

export function getBSPNodeCount(node: NodeBSP | null): number {
  if (!node) {
    return 0;
  }

  return 1 + getBSPNodeCount(node.frontNode) + getBSPNodeCount(node.backNode);
}
